import React, {useState, useEffect, useRef} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {Tooltip as PrimeTooltip} from "primereact/tooltip";
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Paginator} from 'primereact/paginator';
import {MultiSelect} from "primereact/multiselect";
import MyPriceChips from './MyPriceChips';
import {SelectButton} from "primereact/selectbutton";
import {Dropdown} from "primereact/dropdown";
import {Sidebar} from "primereact/sidebar";
import {OverlayPanel} from "primereact/overlaypanel";
import {Calendar} from "primereact/calendar";
import {Skeleton} from "primereact/skeleton";
import {ScrollPanel} from "primereact/scrollpanel";
import {ProgressBar} from "primereact/progressbar";
import {Tag} from "primereact/tag";
import {ListBox} from "primereact/listbox";
import {confirmDialog} from "primereact/confirmdialog";
import {Dialog} from "primereact/dialog";
import {InputNumber} from "primereact/inputnumber";

import {exportFile, formatCurrency, formatDecimal, getActivePriceSendHold, getHostName, getKeyValue, isCampaignDropShipping,
    isCampaignManufacturer, isCampaignMarketSearch, isCampaignSeller, isEfsElektronikUser, isPreventSelect, stripTrackingParams, subscription, toTitleCase, user,
} from "../../utils/Utils";
import {toDateString} from "../../utils/TimeUtil";
import {templateRuleSummary} from "../../utils/pricingTemplate";
import {Loading, Tooltip, usePrevious} from "../../utils/InlineComponents";
import {
    exportableFields,
    getSelectedExportableFields,
    getSelectedProductListColumns,
    productListColumns,
    EFS_TIERED_PRICE_COLUMNS,
    job_states
} from "../../utils/Constants";
import CopyToClipboard from "../../utils/CopyToClipboard";

import Banner from "./Banner";
import ProductAdd from "./ProductAdd";
import BuyboxStateBadge from "../Pricing/BuyboxStateBadge";
import ProductBulkOperations from "./ProductBulkOperations";
import ProductDetail from "./ProductDetail";
import ProductEdit from "./ProductEdit";
import ScrapeStatus from "./ScrapeStatus";
import Number from "../Common/Number";

import {ExportService} from "../../service/ExportService";
import {ProductService} from '../../service/ProductService';
import {ScraperService} from "../../service/ScraperService";
import {DynamicPricingTemplateService} from "../../service/DynamicPricingTemplateService";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import { scroller, Element} from 'react-scroll'
import queryString from "query-string";
import ProductMerge from "./ProductMerge";

import {getProfile, PROFILE_KEYS, isColumnAllowedForProfile} from './profile/productProfiles';
import {tabIcon} from './productDetailProfile';
import ProductToolbar from './ProductToolbar';
import ProductCard from './ProductCard';
import ShopifyProductDialog from "../Campaign/ShopifyProductDialog";
import MarketplaceProductDialog from "../Campaign/MarketplaceProductDialog";
import ProductFilterChips from './ProductFilterChips';
import ProductSelectionBar from './ProductSelectionBar';
import ProductFilterPanel from './ProductFilterPanel';
import ColumnPicker from './ColumnPicker';
import ApplyTemplateDialog from '../Pricing/ApplyTemplateDialog';
import StatusCell from './signals/StatusCell';
import SuggestionAction from './signals/SuggestionAction';
import ViolatorRows from './signals/ViolatorRows';
import CompetitorRows from './signals/CompetitorRows';
import {marketplaceColumns, marketplaceStatusColumnProps, MarketplaceStatusLines} from './marketplace/MarketplaceColumns';
import MarketplaceRows from './marketplace/MarketplaceRows';
import {orderedMarketplaceKeys, marketplaceQueryFilters, marketplaceLabel, ownAwareMarketplaceLabel, marketplaceCurrency, operatorTextOptions, cheaperOnCountsCache, cheaperOnOptionList, hasMarketplaceFilter} from './marketplace/marketplaceProfile';
import {readMarketplaceOperator} from '../Campaign/marketplaceOperatorSettings';
import MarketplaceSellersDialog from '../Dashboard/MarketplaceSellersDialog';
import {violations, pricePosition} from './signals/productSignals';
import {loadProductPages} from './productPaging';
import './product-ux.css';

// A product's category path. Products whose spider captures the whole
// hierarchy carry it outright; the rest are read through the two flat
// fields, so both kinds filter the same way.
export const getCategoryPath = (product) =>
    ((product.categories && product.categories.length > 0)
        ? product.categories
        : [product.category, product.sub_category]).filter(c => c != null && c !== "");

// Grouped so a filter reads as the hierarchy it is: the top level names the
// group, and every level below it is selectable inside. Choosing a level
// takes everything under it, which is what makes "show me all of Makyaj"
// one click rather than eight.
export const buildCategoryFilterOptions = (products, compareText) => {
    const groups = new Map();
    products.forEach(product => {
        const path = getCategoryPath(product);
        if (path.length === 0) return;
        if (!groups.has(path[0])) groups.set(path[0], new Map());
        const items = groups.get(path[0]);
        path.forEach((level, depth) => {
            const value = path.slice(0, depth + 1).join(' > ');
            if (!items.has(value)) {
                // Inside a group the top level is already named, so an item
                // shows only what it adds to it.
                items.set(value, {label: path.slice(1, depth + 1).join(' > ') || path[0], value});
            }
        });
    });
    return [...groups.entries()]
        .map(([label, items]) => ({
            label,
            items: [...items.values()].sort((a, b) => compareText(a.label, b.label)),
        }))
        .sort((a, b) => compareText(a.label, b.label));
};

// A product matches a selected level, or anything beneath it.
export const matchesCategoryFilter = (product, selected) => {
    if (!selected || selected.length === 0) return true;
    const path = getCategoryPath(product).join(' > ');
    return selected.some(value => path === value || path.startsWith(value + ' > '));
};

// An admin may open an account that has no subscription (impersonation), so the
// stored subscription can be null.
const isTrialPlan = () => {
    const current = subscription();
    return !!current && String(current.type || '').toLowerCase() === 'trial';
};

const Product = (props) => {

    const {t, toast, location, setError} = React.useContext(AppContext);

    const {selectedCampaign} = React.useContext(CampaignContext);

    const [showShopifyDialog, setShowShopifyDialog] = useState(false);
    const shopifyAutoOpened = useRef(false);
    // A Shopify-app user has exactly one (Shopify) campaign, so detect via the
    // user — stable across navigation, even when the campaign object is
    // reloaded without its `settings`. Fall back to the campaign's settings.
    const isShopifyCampaign = !!(
        (user() && user().settings && user().settings.shopify_app && user().settings.shopify_app.shop) ||
        (selectedCampaign && selectedCampaign.settings &&
            (selectedCampaign.settings.shopify_oauth || selectedCampaign.settings.shop))
    );

    // Marketplace API (Buybox) campaigns pull their catalog and competitor data
    // from the marketplace's API rather than manual entry or scraping -- see
    // Campaign.js's isMarketplaceBuybox for the same settings.marketplace_api
    // presence check.
    const [showMarketplaceProductDialog, setShowMarketplaceProductDialog] = useState(false);
    const isBuyboxCampaign = !!(selectedCampaign && selectedCampaign.settings && selectedCampaign.settings.marketplace_api);
    const buyboxMarketplaceKey = selectedCampaign?.settings?.marketplace_api?.marketplace;
    const buyboxMarketplaceName = buyboxMarketplaceKey === 'trendyol' ? 'Trendyol'
        : buyboxMarketplaceKey === 'hepsiburada' ? 'Hepsiburada'
        : buyboxMarketplaceKey ? buyboxMarketplaceKey.charAt(0).toUpperCase() + buyboxMarketplaceKey.slice(1)
        : null;
    // Reuses MarketplaceProductsStep's "manage products" button copy so the
    // wording matches between onboarding and the products page.
    const buyboxManageLabel = t('onboarding.products.marketplace.manage', 'Choose products from your {marketplace} store')
        .replace('{marketplace}', buyboxMarketplaceName || t('wizard.mpConnect.marketplacePicker.label'));

    // Deep-linked from the Shopify app with ?addShopify=1 (used when the merchant
    // hasn't tracked any products yet): open the Shopify product dialog once the
    // campaign is loaded. Hash router → read the query off the hash.
    useEffect(() => {
        if (shopifyAutoOpened.current) return;
        if (!isShopifyCampaign || !(selectedCampaign && selectedCampaign.id)) return;
        const qs = (window.location.hash.split('?')[1]) || '';
        if (new URLSearchParams(qs).get('addShopify') === '1') {
            shopifyAutoOpened.current = true;
            setShowShopifyDialog(true);
        }
    }, [isShopifyCampaign, selectedCampaign]);

    const op = useRef(null);
    const opExcelFields = useRef(null);
    const rowMenu = useRef(null);
    const dtProducts = useRef(null);

    const skeletonArray = new Array(5);

    const pages = {
        list: 'list',
        add: 'add',
        edit: 'edit',
        detail: 'detail'
    };

    const defaultFilters = {
        is_active: true,
        category: [],
        brand: [],
        tags: [],
        discount_types: [],
        website: [],
        name: "",
        ref_id: "",
        codes: "",
        mystock: null,
        cheapestCompetitor: null,
        hasCompetitor: null,
        // supplier filter group. Unlike the rest of this object these are
        // applied by the SERVER (the product summary carries only suppliersCount,
        // never the supplier rows themselves), so changing one refetches the list.
        hasSupplier: null,
        supplierWebsites: [],
        hasMinValue: null,
        // Supplier status: the products that were never matched and/or have no
        // seller in stock on every one of these supplier sites.
        supplierStatusWebsites: [],
        supplierStatuses: [],
        drop_shipping_supplied: null,
        drop_shipping_loss: null,
        reprice: null,
        variant_matching_needed: null,
        myprice: [],
        violationOnly: null,
        unauthorizedOnly: null,
        noMap: null,
        pricing_template: null,
        auto_update_paused: null,
        // marketplace filter group (marketplace operator profile only). Applied
        // by the SERVER like the supplier filters, so a change refetches.
        mp_cheaper_on: null,
        mp_min_diff_pct: null,
        mp_first_party: null,
        mp_status: null,
        mp_buybox: null,
        created_at_start: null,
        created_at_end: null,
        globalFilter:""
    };

    const [products, setProducts] = useState([]);
    const [productsToMerge, setProductsToMerge] = useState([]);
    const [menuRow, setMenuRow] = useState(null);

    const [filteredProducts, setFilteredProducts] = useState([]);

    // The list's size as the API reported it with the first page (null until
    // then, or from an API that does not report it); productLoadId tells the
    // pages of the current load from those of one a refresh or campaign switch
    // replaced.
    const [productTotal, setProductTotal] = useState(null);
    const productLoadId = useRef(0);
    // Counts behind the "Cheaper on" dropdown, {campaignId, counts}: refreshed only by
    // a load made without any mp_* filter, so the numbers do not shrink to a filtered set.
    const [cheaperOnCache, setCheaperOnCache] = useState(null);

    const [scrapingRequests, setScrapingRequests] = useState([]);
    const [filters, setFilters] = useState(defaultFilters);

    // Products of a single category-page import link (the campaign setup screen
    // links here from its import list). Unlike the chips below this one is a
    // server-side filter, so it is read from the URL before the first render -
    // otherwise the first fetch would pull the campaign's whole product list.
    const [importUrl, setImportUrl] = useState(() =>
        queryString.parse((window.location.hash.split('?')[1]) || '').importUrl || '');

    const prevFiltersIsActive = usePrevious(filters.is_active);
    const prevSelectedCampaign = usePrevious(selectedCampaign);




    const [brands, setBrands] = useState(null);
    const [categories, setCategories] = useState(null);
    const [filterWebSites, setFilterWebSites] = useState(null);
    const [filterSupplierWebSites, setFilterSupplierWebSites] = useState([]);
    const [filterBrands, setFilterBrands] = useState(null);
    const [filterCategories, setFilterCategories] = useState(null);

    const [tags, setTags] = useState(null);
    const [filterTags, setFilterTags] = useState(null);

    const [discount_types, setDiscountTypes] = useState(null);
    const [filterDiscountTypes, setFilterDiscountTypes] = useState(null);

    const [selectedProduct, setSelectedProduct] = useState({});
    const [selectedProductId, setSelectedProductId] = useState("");
    const [detailInitialTab, setDetailInitialTab] = useState(null);
    const [addSellerRow, setAddSellerRow] = useState(null);
    const [addSellerUrl, setAddSellerUrl] = useState("");
    const [exportFields, setExportFields] = useState(getSelectedExportableFields(t, selectedCampaign, false));

    const profile = getProfile(selectedCampaign);
    const isMarketplaceProfile = profile.key === PROFILE_KEYS.MARKETPLACE;
    const marketplaceConfig = readMarketplaceOperator(selectedCampaign?.settings);
    // The per-marketplace sellers dialog of the marketplace profile:
    // {row, key, visible}. The row stays after hiding so the dialog can close.
    const [mpSellersDialog, setMpSellersDialog] = useState(null);
    // The rows the DataTable shows, read by the marketplace columns' sortFunction
    // at sort time (DataTable keeps calling the last clicked column's function).
    const tableRowsRef = useRef([]);
    // Suppliers are only part of a campaign when its supplier module is on: the
    // whole supplier filter group hangs off this flag.
    const isSupplierModuleActive = !!selectedCampaign?.settings?.supplier_module?.is_active;

    const [selectedColumns, setSelectedColumns] = useState(getSelectedProductListColumns(t, selectedCampaign, false, profile.columnGroups));
    // A selected column, unless the profile hides it (persisted selections and "always" columns
    // would otherwise bring back the marketplace profile's flat seller aggregates). A function,
    // not an inline condition: more `&&` branches in the JSX overflow the rules-of-hooks path count.
    const isColumnShown = (key) => selectedColumns.includes(key) && isColumnAllowedForProfile(profile.key, key);
    const [expandedRows, setExpandedRows] = useState(null);
    const [sellersDialogRow, setSellersDialogRow] = useState(null);
    const isSellerRowExpanded = (rowData) => Array.isArray(expandedRows) && expandedRows.some(r => r.id === rowData.id);
    const toggleSellerRow = (rowData) => {
        setExpandedRows(prev => {
            const arr = Array.isArray(prev) ? prev : [];
            return arr.some(r => r.id === rowData.id) ? arr.filter(r => r.id !== rowData.id) : [...arr, rowData];
        });
    };
    // Inline sellers expander: a chevron + seller count that toggles the row.
    const sellerExpander = (rowData) => {
        const expanded = isSellerRowExpanded(rowData);
        const count = rowData.competitorsCount != null ? rowData.competitorsCount : (rowData.competitors ? rowData.competitors.length : 0);
        return <button type="button" className={"pux-expander" + (expanded ? " open" : "")} onClick={(e) => { e.stopPropagation(); toggleSellerRow(rowData); }}>
            <i className="pi pi-users pux-expander-ic" />
            <span>{count}</span>
            <i className="pi pi-chevron-down pux-expander-caret" />
        </button>;
    };
    const [page, setPage] = useState(pages.list);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(localStorage.getItem("paginator") ? parseInt(localStorage.getItem("paginator")) : 10);
    const isTrialSubscription = isTrialPlan();
    const [sortField, setSortField] = useState(isTrialSubscription ? "competitorsCount" : "name");
    const [sortOrder, setSortOrder] = useState(isTrialSubscription ? -1 : 1);

    // Map of dynamic-pricing template id -> name, loaded once on mount so the
    // product list can resolve `pricing_template_id` (only the id is in the
    // summary) into a human-readable tag label without an N+1 fetch.
    const [templateNameById, setTemplateNameById] = useState({});
    const [templateById, setTemplateById] = useState({});

    const [viewMode, setViewMode] = useState(() => localStorage.getItem('productViewMode') || 'table');
    const onViewMode = (mode) => { localStorage.setItem('productViewMode', mode); setViewMode(mode); };
    // Narrow-screen one-time default: prefer cards on small screens when the user
    // has not chosen a preference yet. No resize listener — checked once on mount.
    useEffect(() => {
        if (!localStorage.getItem('productViewMode') && window.innerWidth < 900) {
            setViewMode('card');
        }
    }, []);

    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showBulkOperations, setShowBulkOperations] = useState(false);
    const [bulkInitialTab, setBulkInitialTab] = useState(0);
    const [showApplyTemplate, setShowApplyTemplate] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showMergePanel, setShowMergePanel] = useState(false);

    const [editingPrice_productID, setEditingPrice_productID] = useState("");
    const [editingMinPrice_productID, setEditingMinPrice_productID] = useState("");
    const [editingMaxPrice_productID, setEditingMaxPrice_productID] = useState("");
    const [pricingStrategyMinValue, setPricingStrategyMinValue] = useState(0);
    const [pricingStrategyMaxValue, setPricingStrategyMaxValue] = useState(0);
    const [violationStrategyMinValue, setViolationStrategyMinValue] = useState(0);

    const productService = new ProductService();
    const scraperService = new ScraperService();
    const exportService = new ExportService();

    const toComparableText = (value) => {
        if (value === null || value === undefined)
            return "";
        if (typeof value === "string")
            return value;
        if (typeof value === "object")
            return JSON.stringify(value);
        return value.toString();
    };

    const compareText = (a, b) => toComparableText(a).localeCompare(toComparableText(b), undefined, {sensitivity: 'base'});

    const setFiltersFromQueryString = () => {
        {
            // HashRouter: read the query off window.location.hash directly. On a
            // fresh page load (a dashboard deep-link opened in a new tab) the
            // AppContext location can still be undefined when this mount effect
            // runs, which silently dropped the filter. window.location.hash is
            // always current.
            let params = queryString.parse((window.location.hash.split('?')[1]) || '');
            if (params) {

                if (params.isActiveOptions && params.isActiveOptions === 'false')
                    filters.is_active = false;

                if (params.myprice)
                    filters.myprice = params.myprice.split(',');

                if (params.reprice && params.reprice === 'false')
                    filters.reprice = false;
                else if (params.reprice && params.reprice === 'true')
                    filters.reprice = true;

                if (params.mystock && params.mystock === 'stock_out')
                    filters.mystock = 'stock_out';
                else if (params.mystock && params.mystock === 'stock_in')
                    filters.mystock = 'stock_in';
                else if (params.mystock && params.mystock === 'invalid_link')
                    filters.mystock = 'invalid_link';

                if (params.new && params.new === 'true') {
                    filters.created_at_start = new Date(new Date().setDate(new Date().getDate() - 7));
                    filters.created_at_end = new Date();
                }

                if (params.cheapestCompetitor)
                    filters.cheapestCompetitor = params.cheapestCompetitor;

                if (params.hasCompetitor && params.hasCompetitor === 'false')
                    filters.hasCompetitor = false;
                else if (params.hasCompetitor && params.hasCompetitor === 'true')
                    filters.hasCompetitor = true;

                if (params.drop_shipping_supplied && params.drop_shipping_supplied === 'false')
                    filters.drop_shipping_supplied = false;
                else if (params.drop_shipping_supplied && params.drop_shipping_supplied === 'true')
                    filters.drop_shipping_supplied = true;

                if (params.drop_shipping_loss && params.drop_shipping_loss === 'false')
                    filters.drop_shipping_loss = false;
                else if (params.drop_shipping_loss && params.drop_shipping_loss === 'true')
                    filters.drop_shipping_loss = true;

                if (params.variant_matching_needed && params.variant_matching_needed === 'false')
                    filters.variant_matching_needed = false;
                else if (params.variant_matching_needed && params.variant_matching_needed === 'true')
                    filters.variant_matching_needed = true;

                // Brand-protection deep links: MAP/MRP violation and unauthorized-seller
                // filters (the dashboard attention cards link here).
                if (params.violation && ['map', 'mrp', 'any', 'none'].indexOf(params.violation) !== -1)
                    filters.violationOnly = params.violation;

                if (params.unauthorized && params.unauthorized === 'true')
                    filters.unauthorizedOnly = true;

                // "MAP not defined": products with no MAP floor (violation_strategy.min_value).
                if (params.noMap && params.noMap === 'true')
                    filters.noMap = true;

                // New object reference so React always re-renders (the block
                // above mutates the existing filters object in place; passing the
                // same reference could bail out of the update on a fresh load).
                setFilters({...filters});
            }
        }
    };

    useEffect(() =>{
        setFiltersFromQueryString();
    }, []);

    // Load the dynamic-pricing templates once and build an {id: name} lookup map.
    // On failure we simply keep an empty map so applied-template tags fall back
    // to a generic label rather than crashing.
    useEffect(() => {
        new DynamicPricingTemplateService().list()
            .then(templates => {
                const nameMap = {};
                const byId = {};
                (templates || []).forEach(tpl => { nameMap[tpl.id] = tpl.name; byId[tpl.id] = tpl; });
                setTemplateNameById(nameMap);
                setTemplateById(byId);
            })
            .catch(() => { setTemplateNameById({}); setTemplateById({}); });
    }, []);

    useEffect(() =>{
        // Only clear filters when the user actually switches to a different
        // campaign (a real previous campaign existed). On the initial load
        // prevSelectedCampaign is undefined; clearing then would wipe deep-link
        // filters parsed from the URL (e.g. ?new=true opened in a new tab).
        // The campaign has to have actually changed: this effect also re-runs
        // when the import-link filter is removed, and clearing there would take
        // every other filter the user set with it.
        if (prevSelectedCampaign && prevSelectedCampaign !== selectedCampaign && prevFiltersIsActive === filters.is_active)
            onClear();
        refresh();
        // The supplier filters are server-side, so each one has to refetch
        // instead of re-running applyFilters. The lists are joined rather than
        // passed as the arrays themselves: a fresh [] reference on every filter
        // reset would refetch for no reason.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCampaign, filters.is_active, importUrl,
        filters.hasSupplier, (filters.supplierWebsites || []).join(','), filters.hasMinValue,
        (filters.supplierStatusWebsites || []).join(','), (filters.supplierStatuses || []).join(','),
        filters.mp_cheaper_on, filters.mp_min_diff_pct, filters.mp_first_party,
        filters.mp_status, filters.mp_buybox]);

    // The supplier-site dropdown's options: the campaign's supplier hosts, read
    // campaign-wide so they survive a list already narrowed to one of them.
    useEffect(() => {
        if (!(selectedCampaign && selectedCampaign.id) || !isSupplierModuleActive) {
            setFilterSupplierWebSites([]);
            return;
        }
        productService.getSupplierWebsites(selectedCampaign.id)
            .then(websites => setFilterSupplierWebSites((websites || [])
                .map(website => ({label: website, value: website}))))
            .catch(() => setFilterSupplierWebSites([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCampaign, isSupplierModuleActive]);

    useEffect(() =>{
        applyFilters();
    }, [products]);

    useEffect(() =>{
        applyFilters();
    }, [filters, sortOrder, sortField]);

    useEffect(() =>{
        if(page === pages.list && selectedProductId)
            scroller.scrollTo(selectedProductId.toString().split("-").join(""), {
                duration:0,
                smooth: "easeInOutQuart",
                delay:0,
                offset: -200
            });
    }, [page]);

    const applyFilters = () => {
        const result = products.filter(product =>
        {

            return matchesCategoryFilter(product, filters.category) &&
                (filters.brand.length === 0 || (filters.brand.includes(product.brand ?? ""))  ) &&
                (filters.tags.length === 0 || (product.tags && filters.tags.some(tag => product.tags.includes(tag)))) &&
                (filters.discount_types.length === 0 || (Array.isArray(product.discount_types) && filters.discount_types.some(discount_type => product.discount_types.includes(discount_type)))) &&
                (filters.website.length === 0 || (filters.website.includes(product.website ?? ""))  ) &&
                (filters.name === "" || (product.name && product.name.toLowerCase().indexOf(filters.name.toLowerCase()) !== -1)) &&
                (filters.codes === ""
                    || (product.ref_id && product.ref_id.toString().toLowerCase().toString().indexOf(filters.codes.toLowerCase()) !== -1)
                    || (product.sku && product.sku.toString().toLowerCase().toString().indexOf(filters.codes.toLowerCase()) !== -1)
                    || (product.mpn && product.mpn.toString().toLowerCase().toString().indexOf(filters.codes.toLowerCase()) !== -1)
                    || (product.ean && product.ean.toString().toLowerCase().toString().indexOf(filters.codes.toLowerCase()) !== -1)
                    || (product.upc && product.upc.toString().toLowerCase().toString().indexOf(filters.codes.toLowerCase()) !== -1)
                    || (product.asin && product.asin.toString().toLowerCase().toString().indexOf(filters.codes.toLowerCase()) !== -1)
                    || (product.barcode && product.barcode.toString().toLowerCase().toString().indexOf(filters.codes.toLowerCase()) !== -1)) &&
                (filters.mystock === null || (filters.mystock === 'stock_in' && product.stock && product.stock > 0) || (filters.mystock === 'stock_out' && !product.is_url_deactivated && (!product.stock || product.stock <= 0) ) || (filters.mystock === 'invalid_link' && product.is_url_deactivated)) &&
                (filters.reprice === null || (product.price != null && product.suggested_price != null && (
                    (filters.reprice === true && product.suggested_price < product.price) ||
                    (filters.reprice === false && product.suggested_price > product.price)
                ))) &&
                (filters.myprice.length === 0 || (() => {
                    const pos = pricePosition(product);
                    if (!pos) return false;
                    // Map filter option values -> pricePosition levels (same taxonomy as the status chip).
                    const LEVEL_OF = { highest:'highest', avg_higher:'high', high:'high', average:'average', same:'same', avg_lower:'low', lowest:'lowest' };
                    return filters.myprice.some(v => LEVEL_OF[v] === pos.level);
                })()) &&
                (filters.created_at_start === null || (product.created_at && (toDateString(new Date(product.created_at)) >= toDateString(new Date(filters.created_at_start))))) &&
                (filters.created_at_end === null || (product.created_at && (toDateString(new Date(product.created_at)) <= toDateString(new Date(filters.created_at_end))))) &&

                (filters.drop_shipping_supplied === null || (filters.drop_shipping_supplied && product.drop_shipping && product.drop_shipping.stock && product.drop_shipping.stock > 0) || (!filters.drop_shipping_supplied && product.drop_shipping && product.drop_shipping.stock === 0 )) &&
                (filters.drop_shipping_loss === null || (filters.drop_shipping_loss && product.drop_shipping && product.drop_shipping_cost && product.price && product.drop_shipping_cost > product.price) || (!filters.drop_shipping_loss && product.drop_shipping && product.drop_shipping_cost && product.price && product.drop_shipping_cost <= product.price)) &&

                (filters.hasCompetitor === null || (filters.hasCompetitor && product.competitorsCount !== 0) || (!filters.hasCompetitor && product.competitorsCount === 0)) &&
                (filters.variant_matching_needed === null || (((filters.variant_matching_needed && product.variant_matching_needed ) || (!filters.variant_matching_needed && !product.variant_matching_needed)))) &&
                (filters.pricing_template == null
                    || (filters.pricing_template === '__none__' && !product.pricing_template_id)
                    || (product.pricing_template_id === filters.pricing_template)) &&
                (filters.auto_update_paused === null || !!product.is_auto_update_paused === filters.auto_update_paused) &&
                (() => {
                  if (filters.violationOnly == null) return true;
                  const v = violations(product);
                  if (filters.violationOnly === 'any') return v.map || v.mrp;
                  if (filters.violationOnly === 'map') return v.map;
                  if (filters.violationOnly === 'mrp') return v.mrp;
                  if (filters.violationOnly === 'none') return !v.map && !v.mrp;
                  return true;
                })() &&
                (filters.unauthorizedOnly == null || (filters.unauthorizedOnly === true && product.unauthorized_count > 0)) &&
                (filters.noMap == null || (filters.noMap === true && !(product.violation_strategy && product.violation_strategy.min_value))) &&
                (filters.cheapestCompetitor === null || (product.price && product.competitorsMinPrice && product.competitorsMinName && product.competitorsMinName.toLowerCase().indexOf(filters.cheapestCompetitor.toLowerCase()) !== -1  && product.price > product.competitorsMinPrice)) &&
                (filters.globalFilter === "" || (product.name && (new RegExp('^' + ("*"+filters.globalFilter+"*").replace(/\*/g, '.*') + '$').test(product.name.toLowerCase()) || product.name.toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1))
                    || (product.description && product.description.toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.category && product.category.toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.brand && product.brand.toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.ref_id && product.ref_id.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.sku && product.sku.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.mpn && product.mpn.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.ean && product.ean.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.upc && product.upc.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.asin && product.asin.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.barcode && product.barcode.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.competitorsMinName && product.competitorsMinName.toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.competitorsMaxName && product.competitorsMaxName.toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.price && product.price.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.competitorsMinPrice && product.competitorsMinPrice.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (product.competitorsMaxPrice && product.competitorsMaxPrice.toString().toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)
                    || (Array.isArray(product.properties) && JSON.stringify(product.properties.map(p=> p.value)).toLowerCase().indexOf(filters.globalFilter.toLowerCase()) !== -1)


                ) ;
        })

        if (result.length < first)
            setFirst(Math.floor(result.length/rows)*rows);

        if (sortField === "price" ||
            sortField === "stock" ||
            sortField === "mrp" ||
            sortField === "position" ||
            sortField === "ref_id" ||
            sortField === "pricing_strategy.min_value" ||
            sortField === "pricing_strategy.max_value" ||
            sortField === "suggested_price" ||
            sortField === "target_price" ||
            sortField === "review.rating_count" ||
            sortField === "review.rating" ||
            sortField === "competitorsMinPrice" ||
            sortField === "competitorsAvgPrice" ||
            sortField === "competitorsAvgPriceDiff" ||
            sortField === "competitorsMaxPrice" ||
            sortField === "competitorsCount" ||
            sortField === "drop_shipping_price" ||
            sortField === "drop_shipping_cost" ||
            sortField === "drop_shipping_review" ||
            sortField === "drop_shipping_rate" ||
            sortField === "drop_shipping_best_sellers_rank" ||
            sortField === "competitorSuggestionsCount" ||
            sortField === "suppliersCount" ||
            sortField === "supplierSuggestionsCount" ||
            sortField === "mp_first_party_price" ||
            sortField === "mp_buybox_price" ||
            sortField === "mp_own_seller_count" ||
            sortField === "mp_rank" ||
            sortField === "mp_diff_pct" ||
            sortField === "mp_first_party_diff_pct")
        {
            if (sortField.includes('.'))
            {
                const col = sortField.split('.')[0];
                const field = sortField.split('.')[1];
                result.sort(function (a, b) {return sortOrder * (parseFloat(!(a && a[col] && a[col][field]) ? 0 : a[col][field].toString().split(" ")[0]) - parseFloat(!(b && b[col] && b[col][field]) ? 0 : b[col][field].toString().split(" ")[0]));});
            }
            else
                result.sort(function (a, b) {return sortOrder * (parseFloat(!(a && a[sortField]) ? 0 : a[sortField].toString().split(" ")[0]) - parseFloat(!(b && b[sortField]) ? 0 : b[sortField].toString().split(" ")[0]));});

        }
        else if (sortField === "properties")
            result.sort(function(a, b){return sortOrder * compareText(a && a[sortField], b && b[sortField]);});
            // else if (sortField.startsWith("properties."))
            // {
            //     const col = sortField.split('.')[0];
            //     const field = sortField.split('.')[1];
            //     result.sort(function(a, b){
            //         let aValue = a?[col].filter(p=>p.name.toLowerCase()===field.toLowerCase())?[0].value?? "";
            //         let bValue = b?[col].filter(p=>p.name.toLowerCase()===field.toLowerCase())?[0].value?? "";
            //         return sortOrder * aValue.localeCompare(bValue), undefined, {sensitivity: 'base'};
            //     });
        // }
        else
            result.sort(function(a, b){return sortOrder * compareText(a && a[sortField], b && b[sortField]);});

        setFilteredProducts(result);
    }

    //#region Add-Edit Product

    const showAdd = () => {
        setPage(pages.add);
    };

    const showUpdate = (rowData) => {
        setSelectedProduct(rowData);
        setPage(pages.edit);
    };

    const cancel = () => {
        setSelectedProduct({});
        setPage(pages.list);
    }

    const detail = (rowData, tab) => {
        setSelectedProductId(rowData.id);
        setDetailInitialTab(tab || null);
        setPage(pages.detail);
    }

    // Add-seller popup (shown from the Seller-count column when a product has none).
    const openAddSeller = (row) => { setAddSellerRow(row); setAddSellerUrl(""); };
    const saveAddSeller = () => {
        // Drop ad click ids (srsltid, gclid, utm_*, ...) so the stored URL stays clean.
        const url = stripTrackingParams(addSellerUrl);
        if (!url || !addSellerRow) return;
        const competitors = [{ id: "", product_url: url, name: getHostName(url), is_mine: false, prices: [], scraping_request: null, is_active: true }];
        productService.addCompetitor(addSellerRow.id, competitors).then(() => {
            setAddSellerRow(null);
            setAddSellerUrl("");
            refresh();
        }).catch(error => { setError(error); setError(null); });
    };

    const add = (new_products) => {

        new_products.product_url.split('||').forEach(product_url=> {

            let is_exists = products.some(p => p.product_url.toLowerCase() === product_url.toLowerCase());

            if (is_exists) {
                toast.current.show({
                    severity: 'warn',
                    summary: t('product.product_exist_summary'),
                    detail: t('product.product_exist_detail')
                });
                return;
            }
            let addedProduct = {campaign_id: selectedCampaign.id, competitors: []};

            addedProduct.competitors.push({
                product_url: product_url,
                is_mine: true,
            });

            productService.addProduct(addedProduct).then(addedProduct => {
                addedProduct.product_url = product_url;
                products.push(addedProduct);
                scrapingRequests[addedProduct.id] = addedProduct.scraping_request;
                setProducts([...products]);
                setScrapingRequests({...scrapingRequests});
                setPage(pages.list);

                toast.current.show({
                    severity: 'success',
                    summary: t('product.add_product'),
                    detail: t('message_detail.successful')
                });
            }).catch(error => {
                setError(error);
                setError(null);
            });
        })


    }

    const update = (product) =>{

        productService.updateProduct(product.id, product).then(updatedProduct => {

            let index = products.findIndex(p=> p.id === product.id);

            products[index].name = product.name;
            products[index].brand = product.brand;
            products[index].category = product.category;
            products[index].barcode = product.barcode;
            products[index].sku = product.sku;
            products[index].mpn = product.mpn;
            products[index].ean = product.ean;
            products[index].upc = product.upc;
            products[index].asin = product.asin;
            products[index].tags = product.tags;
            products[index].ref_id = product.ref_id;
            products[index].vat = product.vat;
            products[index].search_key = product.search_key;
            products[index].properties = product.properties;
            products[index].campaign_id = product.campaign_id;
            products[index].is_active = product.is_active;

            setProducts([...products]);
            setPage(pages.list);


            toast.current.show({
                severity: 'success',
                summary: t('product.edit_product'),
                detail: t('message_detail.successful')
            });
        }).catch(error => {
            setError(error);
            setError(null);
        });
    }

    const mergeProducts = () => {
        productService.mergeProducts(productsToMerge).then(mergedProducts=>{
            setProducts([...products.filter((product)=>productsToMerge.findIndex(p=> p.keepAsMine === false && p.id===product.id) === -1)]);
            setProductsToMerge([]);
            toast.current.show({
                severity: 'success',
                summary: t('actions.merge_products'),
                detail: t('message_detail.successful')
            });
        }).catch(error => {
            setError(error);
            setError(null);
        });
    }

    const onFilterChange = (e) =>{
        const { key, value } = getKeyValue(e);

        setFilters({...filters, [key]:value});
    };
    //#endregion Add-Edit Product

    //#region List Actions

    const getNotDoneProductScrapingRequests = () => {
        if (selectedCampaign && selectedCampaign.id) {
            scraperService.getNotDoneProductScrapingRequests(selectedCampaign.id).then(scrapingRequests => {
                setScrapingRequests(scrapingRequests);
            }).catch(error => {
                setError(error);
                setError(null);
            });
        }
    }

    // The supplier filter group as request params. Off (and therefore absent
    // from the query) while the campaign's supplier module is inactive, so a
    // stale filter can never narrow the list of a campaign that has no
    // suppliers at all.
    const supplierQueryFilters = () => {
        if (!isSupplierModuleActive)
            return {};
        return {
            has_supplier: filters.hasSupplier === null ? '' : String(filters.hasSupplier),
            supplier_websites: (filters.supplierWebsites || []).join(','),
            has_min_value: filters.hasMinValue === null ? '' : String(filters.hasMinValue),
            supplier_status_websites: (filters.supplierStatusWebsites || []).join(','),
            supplier_status: (filters.supplierStatuses || []).join(','),
        };
    };

    // Shows the products loaded so far and rebuilds the filter options from them.
    // Batched: React 17 renders once per setState outside its own event handlers,
    // and this runs once per arriving page.
    const showLoadedProducts = (allProducts) => {
        unstable_batchedUpdates(() => {
            const distBrands = [...new Set(allProducts.map(allProducts => allProducts.brand ? allProducts.brand : ""))].sort(compareText);
            const distFilterBrands = [...new Set(allProducts.map(p => p.brand).filter(b => b != null && b !== ""))].map(brand => {return {"label":brand, "value": brand}}).sort((a, b) => compareText(a && a.label, b && b.label));

            const distCategories = [...new Set(allProducts.map(allProducts => allProducts.category ? allProducts.category : ""))].sort(compareText);
            const distFilterCategories = buildCategoryFilterOptions(allProducts, compareText);

            const distFilterWebSites = [...new Set(allProducts.map(p => p.website).filter(w => w != null && w !== ""))].map(website => {return {"label":website, "value": website}}).sort((a, b) => compareText(a && a.label, b && b.label));

            // Guard against products with missing tags/discount_types: flatMap over a
            // missing field yields `undefined`, which (with a loose null filter) would
            // leak a {label: undefined} option and crash the filterable MultiSelect
            // (label.toLowerCase()). Use `|| []` and drop null/undefined/empty values.
            const flattenTags = [... new Set(allProducts.flatMap(product => product.tags || []))]
            const distTags = flattenTags.filter(a=> a != null && a !== '').sort(compareText);
            const distFilterTags = flattenTags.filter(a=> a != null && a !== '').map(tag => {return {"label":tag, "value": tag}}).sort((a, b) => compareText(a && a.label, b && b.label));

            const flattenDiscountTypes = [... new Set(allProducts.flatMap(product => product.discount_types || []))]
            const distDiscountTypes = flattenDiscountTypes.filter(a=> a != null && a !== '').sort(compareText);
            const distFilterDiscountTypes = flattenDiscountTypes.filter(a=> a != null && a !== '').map(discount_type => {return {"label":discount_type, "value": discount_type}}).sort((a, b) => compareText(a && a.label, b && b.label));

            setProducts(allProducts);
            if (isMarketplaceProfile)
                setCheaperOnCache(prev => ({
                    campaignId: selectedCampaign.id,
                    counts: cheaperOnCountsCache(prev && prev.campaignId === selectedCampaign.id ? prev.counts : null,
                                                 allProducts, hasMarketplaceFilter(filters)),
                }));
            setExpandedRows(null);
            setCategories(distCategories);
            setFilterCategories(distFilterCategories);
            setBrands(distBrands);
            setFilterBrands(distFilterBrands);
            setFilterWebSites(distFilterWebSites);

            setTags(distTags);
            setFilterTags(distFilterTags);

            setDiscountTypes(distDiscountTypes);
            setFilterDiscountTypes(distFilterDiscountTypes);
            if (allProducts.length > 0)
                setLoading(false);
        });
    };

    const refresh = () => {
        if (!(selectedCampaign && selectedCampaign.id))
            return;

        const loadId = ++productLoadId.current;
        const isStale = () => productLoadId.current !== loadId;
        setLoading(true);
        setLoadingMessage(true);
        setProductTotal(null);
        setProducts([]);
        setProductsToMerge([]);
        getNotDoneProductScrapingRequests();

        let loadedProducts = [];
        // The marketplace filters only apply in the marketplace profile, so a
        // stale value can never narrow another campaign's list.
        const listFilters = {...supplierQueryFilters(),
                             ...(isMarketplaceProfile ? marketplaceQueryFilters(filters) : {})};
        loadProductPages({
            fetchPage: startIndex => productService.getProducts("", "", filters.is_active, "", selectedCampaign.id,
                                                                startIndex, importUrl, listFilters, false),
            fetchTotal: () => productService.getProductCount("", "", filters.is_active, "", selectedCampaign.id,
                                                             importUrl, listFilters),
            isStale,
            onTotal: total => setProductTotal(total),
            onPage: pList => {
                loadedProducts = loadedProducts.concat(pList.map(p => {
                    return {...p, "website" : getHostName(p.product_url && p.product_url !== "no url" ? p.product_url : p.competitors?.find(c=> c.is_mine === true)?.product_url)}
                }));
                showLoadedProducts(loadedProducts);
            },
        }).then(() => {
            if (isStale())
                return;
            setLoadingMessage(false);
            setLoading(false);
        }).catch(error => {
            if (isStale())
                return;
            // Stop the pages still in flight from filling the list back in.
            productLoadId.current++;
            setError(error);
            setError(null);
            setLoading(false);
            setLoadingMessage(false);
            setProducts([]);
        });
    };

    const onClear = () => {
        setFilters({...defaultFilters});
        setImportUrl('');
    };

    const confirmDeleteProduct = (rowData) => {
        confirmDialog({
            message: t('dialogs.product.delete_msg'),
            header: t('dialogs.product.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => deleteProduct(rowData),
            acceptLabel: t('yes_no.yes'),
            rejectLabel: t('yes_no.no')
        });
    };

    const deleteProduct = (rowData) => {
        productService.deleteProduct(rowData).then(() => {
            setProducts(products.filter(product => product.id !== rowData.id));
            toast.current.show({severity: 'success', summary: t('actions.delete'), detail: t('message_detail.successful')})
        }).catch(error =>{
            setError(error);
            setError(null);
        });
    };

    const changeIsActive = (rowData) => {
        productService.updateProductIsActive(rowData.id, rowData.is_active).then(() => {
            products.find(product => product.id === rowData.id).is_active = !rowData.is_active;
            setProducts([...products]);
            toast.current.show({severity: 'success', summary: rowData.is_active ? t('product.activated') : t('product.deactivated'), detail: t('message_detail.successful')})
        }).catch(error =>{
            setError(error);
            setError(null);
        });
    };

    const bulkProductIds = () => selectedRows.map(s => s.id);
    const bulkSetActive = (deactivate) => {
        if (!selectedRows.length) return;
        const data = { campaign_id: selectedCampaign.id, product_ids: bulkProductIds() };
        productService.updateAllProductsIsActive(data, deactivate).then(() => {
            toast.current.show({severity: 'success', summary: deactivate ? t('actions.deactivate') : t('actions.activate'), detail: t('message_detail.successful')});
            setSelectedRows([]);
            refresh();
        }).catch(e => { setError(e); setError(null); });
    };
    const bulkRemove = () => {
        if (!selectedRows.length) return;
        confirmDialog({
            message: t('dialogs.product.delete_msg'),
            header: t('dialogs.product.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('yes_no.yes'),
            rejectLabel: t('yes_no.no'),
            accept: () => {
                const data = { campaign_id: selectedCampaign.id, product_ids: bulkProductIds() };
                productService.removeAllProducts(data).then(() => {
                    toast.current.show({severity: 'success', summary: t('actions.remove'), detail: t('message_detail.successful')});
                    setSelectedRows([]);
                    refresh();
                }).catch(e => { setError(e); setError(null); });
            },
        });
    };
    // Fully remove the applied pricing template from the selected products
    // (clears strategy + tag, async). Only templated products are affected.
    // Patch only the affected rows in place (no full list refresh) after a
    // template apply/detach. Mirrors the inline strategy-editor pattern: mutate
    // the product objects (filteredProducts shares the same refs) then re-set.
    const patchAffectedProducts = (updates) => {
        if (!updates || !updates.length) return;
        updates.forEach(u => {
            const p = products.find(x => x.id === u.id);
            if (p) {
                p.pricing_template_id = u.pricing_template_id;
                p.pricing_strategy = u.pricing_strategy;
                p.suggested_price = u.suggested_price;
                p.target_price = u.target_price;
            }
        });
        setProducts([...products]);
    };

    const bulkDetachTemplate = () => {
        if (!selectedRows.length) return;
        confirmDialog({
            message: t('pricing_template.remove_confirm'),
            header: t('pricing_template.remove'),
            icon: 'pi pi-exclamation-triangle',
            // Constrain width so the message wraps instead of stretching full-screen.
            style: {width: '460px', maxWidth: '92vw'},
            acceptClassName: 'p-button-danger',
            acceptLabel: t('yes_no.yes'),
            rejectLabel: t('yes_no.no'),
            accept: () => {
                new DynamicPricingTemplateService().detach(bulkProductIds()).then((res) => {
                    if (res && res.success === false) { setError(new Error(res.error || 'error')); setError(null); }
                    else { toast.current.show({severity: 'success', summary: t('pricing_template.remove'), detail: t('pricing_template.remove_done')}); patchAffectedProducts(res && res.products); }
                    setSelectedRows([]);
                }).catch(e => { setError(e); setError(null); });
            },
        });
    };

    const selectProduct = (e) => {
        if (!loading && !isPreventSelect(e.originalEvent.target)){
            if (e.data)
                detail(e.data);
            //else if (selectedProductId)
            //    detail(selectedProductRow());
        }
    };

    const selectedProductRow = () => {
        if (selectedProductId)
            return products.find(product=> product.id === selectedProductId);
        else
            return null;
    };

    const detailComplete = () => {
        setPage(pages.list);
    };

    const goToPrevious = () => {
        let index = filteredProducts.findIndex(p=> p.id === selectedProductId);
        let newIndex = (index === 0 ? filteredProducts.length - 1 : index - 1);

        setSelectedProductId(filteredProducts[newIndex].id);
        setFirst(Math.floor(newIndex/rows)*rows);
    };

    const goToNext = () => {
        let index = filteredProducts.findIndex(p=> p.id === selectedProductId);
        let newIndex = (index === filteredProducts.length - 1 ? 0 : index + 1);

        setSelectedProductId(filteredProducts[newIndex].id);
        setFirst(Math.floor(newIndex/rows)*rows);
    };

    const exportList = () => {
        exportService.exportProductList(selectedCampaign.id, exportFields, exportableFields(t, selectedCampaign), filters).then((response) => {
            exportFile(response, selectedCampaign.name + "_" + t('menu_item.products') + "_" + toDateString(new Date()) + ".xlsx");
        }).catch(error =>{
            setError(error);
            setError(null);
        });
    }

    // Marketplace profile Excel: a fixed column set built by the backend, so no
    // field picker. labels are the translated headers keyed by the backend's ids.
    const exportMarketplaceList = () => {
        const operator = operatorTextOptions(selectedCampaign?.settings);
        const label = (key) => t('product.marketplace.' + key, operator);
        const labels = {
            product_code: t('product.product_code'),
            name: t('product.product_name'),
            brand: t('product.brand'),
            category: t('product.category'),
            first_party_price: label('first_party_price'),
            buybox_price: label('buybox_price'),
            buybox_holder: label('buybox_holder'),
            own_seller_count: label('own_seller_count'),
            rank: label('rank'),
            best_price: label('best_price'),
            best_marketplace: label('best_marketplace'),
            diff_pct: label('diff_pct'),
            first_party_diff_pct: label('first_party_diff_pct'),
            product_url: label('product_url'),
            marketplace_min: label('marketplace_min'),
            marketplace_seller: label('marketplace_seller'),
            marketplace_sellers: label('marketplace_sellers'),
            marketplace_dispatch_days: label('marketplace_dispatch_days'),
            marketplace_delivery_date: label('marketplace_delivery_date'),
            extra: label('extra'),
        };
        const exportFilters = {
            is_active: filters.is_active,
            name: filters.name,
            category: filters.category,
            brand: filters.brand,
            ...marketplaceQueryFilters(filters),
        };
        exportService.exportMarketplaceOperatorList(selectedCampaign.id, exportFilters, labels).then((response) => {
            exportFile(response, selectedCampaign.name + "_" + t('menu_item.products') + "_" + toDateString(new Date()) + ".xlsx");
        }).catch(error =>{
            setError(error);
            setError(null);
        });
    };

    //#endregion ListAction

    //#region DataTable Items

    const indexTemplate = (data, props) => {
        if (loading)
            return <Skeleton />;
        return <Element name={data.id.toString().split("-").join("")} style={{textAlign:'right'}}>{props.rowIndex + 1}</Element>;
    }

    const productImageTemplate = (rowData) => {
        if (loading)
            return <Skeleton size="7rem"/>;
        return <div>
            <span className="p-column-title">{t('product.image')}</span>
            <img referrerPolicy='no-referrer' className="img-fluid-thumb" src={rowData.image} alt=""/>
        </div>;
    }

    const productWebsiteTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title">{t('product.website')}</span>
            {rowData.website}
        </div>;
    };

    const asinTemplate = (rowData) => {
        const style = selectedColumns.includes("asin") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return rowData.asin ? <React.Fragment>
            <span className="p-column-title" style={style}>{t('product.asin')}</span>
            {rowData.asin}
            <CopyToClipboard textToCopy={rowData.asin} />
        </React.Fragment> : null;
    };

    const eanTemplate = (rowData) => {
        const style = selectedColumns.includes("ean") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return rowData.ean ? <React.Fragment>
            <span className="p-column-title" style={style}>{t('product.ean')}</span>
            {rowData.ean}
            <CopyToClipboard textToCopy={rowData.ean} />
        </React.Fragment> : null;
    };

    const mpnTemplate = (rowData) => {
        const style = selectedColumns.includes("mpn") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return rowData.mpn ? <React.Fragment>
            <span className="p-column-title" style={style}>{t('product.mpn')}</span>
            {rowData.mpn}
            <CopyToClipboard textToCopy={rowData.mpn} />
        </React.Fragment> : null;
    };

    const upcTemplate = (rowData) => {
        const style = selectedColumns.includes("upc") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return rowData.upc ? <React.Fragment>
            <span className="p-column-title" style={style}>{t('product.upc')}</span>
            {rowData.upc}
            <CopyToClipboard textToCopy={rowData.upc} />
        </React.Fragment> : null;
    };

    const skuTemplate = (rowData) => {
        const style = selectedColumns.includes("sku") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return rowData.sku ? <React.Fragment>
            <span className="p-column-title" style={style}>{t('product.sku')}</span>
            {rowData.sku}
            <CopyToClipboard textToCopy={rowData.sku} />
        </React.Fragment> : null;
    };

    const barcodeTemplate = (rowData) => {
        const style = selectedColumns.includes("barcode") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return rowData.barcode ? <React.Fragment>
            <span className="p-column-title" style={style}>{t('product.barcode')}</span>
            {rowData.barcode}
            <CopyToClipboard textToCopy={rowData.barcode} />
        </React.Fragment> : null;
    };

    // The scraping failures the customer can act on rather than wait out: the product
    // they added is already tracked, or its page is gone. Both leave an empty row behind
    // that no re-run will ever fill, so the row says what happened and offers the way out.
    // Every other failure -- a ban, a parse error -- is retryable and keeps the generic
    // "will be captured shortly" line.
    const getActionableFailure = (scraping_request) => {
        if (!scraping_request || scraping_request.state !== job_states.failed)
            return null;
        for (const failure of scraping_request.failure_details || []) {
            const detail = failure.exception_detail || {};
            const message = failure.exception || "";
            if (detail.code === "duplicate_product_code")
                return {kind: "duplicate", code: detail.product_code, existing_id: detail.existing_product_id, existing_name: detail.existing_product_name};
            if (detail.code === "product_not_found")
                return {kind: "not_found", url: detail.product_url || failure.url || scraping_request.url};
            // Failures recorded before the structured codes existed. The duplicate's
            // product code is still in the message, but the existing product's id was
            // never stored, so those rows get the warning without a link.
            const duplicateMatch = /Product with the same product code \(([^)]*)\) already exists/.exec(message);
            if (duplicateMatch)
                return {kind: "duplicate", code: duplicateMatch[1], existing_id: null, existing_name: null};
            const notFoundMatch = /Product not found \(HTTP \d+\): (\S+)/.exec(message);
            if (notFoundMatch)
                return {kind: "not_found", url: notFoundMatch[1]};
        }
        return null;
    };

    // A link that carries the weight of a button next to the delete one.
    const actionableFailureLink = (href, label, icon, tooltip) => {
        return <a href={href} target="_blank" rel="noopener noreferrer" title={tooltip}
                  className="p-button p-component p-button-outlined p-button-sm p-mr-2" style={{textDecoration: "none"}}>
            <span className={"p-button-icon p-button-icon-left pi " + icon}/>
            <span className="p-button-label">{label}</span>
        </a>;
    };

    const actionableFailureTemplate = (rowData, failure) => {
        // preventSelect: neither the link nor the delete button may also open the row detail.
        return <div className="preventSelect" style={{color: "#dc2626"}}>
            <div>{failure.kind === "duplicate" ?
                t('product.duplicate_product_code', {code: failure.code}) :
                t('product.product_not_found', {website: getHostName(failure.url)})}</div>
            <div style={{marginTop: ".35em", display: "flex", alignItems: "center", flexWrap: "wrap"}}>
                {failure.kind === "duplicate" && failure.existing_id &&
                    actionableFailureLink("/#/productdetail?id=" + failure.existing_id, t('product.duplicate_open_existing'), "pi-external-link", failure.existing_name)}
                {failure.kind === "not_found" && failure.url &&
                    actionableFailureLink(failure.url, t('product.product_not_found_check_link'), "pi-external-link", failure.url)}
                <Button label={t('actions.delete')} icon="pi pi-trash" className="p-button-outlined p-button-danger p-button-sm"
                        onClick={() => confirmDeleteProduct(rowData)}/>
            </div>
        </div>;
    };

    const productNameTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        const scraping_request = scrapingRequests[rowData.id]
        const actionableFailure = rowData.name && rowData.name !== "" ? null : getActionableFailure(scraping_request);
        return  <React.Fragment>
            <span className="p-column-title">{t('product.product_name')}</span>
            {rowData.name && rowData.name !== "" ? rowData.name : actionableFailure ? actionableFailureTemplate(rowData, actionableFailure) :
                <span style={{color: scraping_request && scraping_request.state && scraping_request.state === job_states.in_preparation ? "deepskyblue" : "orange"}}>{
                    t(scraping_request && scraping_request.state && scraping_request.state === job_states.in_preparation ? 'product.site_to_be_scraped' : 'product.product_to_be_scraped', {website: getHostName(scraping_request?.url)})}</span>
                }
            {rowData.name && <CopyToClipboard textToCopy={rowData.name} />}
            {/* The path spells out both flat fields, so a product that has one
                shows it alone rather than the same words three times over. */}
            {rowData.categories?.length > 0
                ? (!selectedColumns.includes("categories") && <div>{categoriesTemplate(rowData)}</div>)
                : <>
                    {!selectedColumns.includes("category") && rowData.category && <div>{categoryTemplate(rowData)}</div>}
                    {!selectedColumns.includes("sub_category") && rowData.sub_category && <div>{subCategoryTemplate(rowData)}</div>}
                  </>}
            {!selectedColumns.includes("type") && rowData.type && <div>{typeTemplate(rowData)}</div>}
            {!selectedColumns.includes("brand") && rowData.brand && <div>{brandTemplate(rowData)}</div>}
            {!selectedColumns.includes("sku") && rowData.sku && <div>{skuTemplate(rowData)}</div>}
            {!selectedColumns.includes("mpn") && rowData.mpn && <div>{mpnTemplate(rowData)}</div>}
            {!selectedColumns.includes("ean") && rowData.ean && <div>{eanTemplate(rowData)}</div>}
            {!selectedColumns.includes("upc") && rowData.upc && <div>{upcTemplate(rowData)}</div>}
            {!selectedColumns.includes("asin") && rowData.asin && <div>{asinTemplate(rowData)}</div>}
            {!selectedColumns.includes("barcode") && rowData.barcode && <div>{barcodeTemplate(rowData)}</div>}
            {!selectedColumns.includes("properties") && rowData.properties && <div>{variantTemplate(rowData)}</div>}
        </React.Fragment>;
    };

    const variantTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        let variantList = null;
        if (Array.isArray(rowData.properties)) {
            variantList = rowData.properties.map(property => {



                if (property.value !== "" && !selectedCampaign?.settings?.special_fields?.some(sf=> sf.name === "properties." + property.name?.toLowerCase() && sf.selected)
                && property.name?.toLowerCase() !== "brand" && property.name?.toLowerCase() !== "category" && property.name?.toLowerCase() !== "description")
                    return <div key={property.name}><span className="p-column-title" style={{display:"inline-block"}}>{t("product.properties." + property.name?.toLowerCase()).replace('product.properties.', '')}</span>{property.value}</div>;
            });
        }
        return variantList;
    };

    const productDescriptionTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <ScrollPanel style={{height: '110px', fontSize:"0.8em"}}>
            <span className="p-column-title">{t('product.description')}</span>
            <div dangerouslySetInnerHTML={{__html: rowData.description}}></div>
        </ScrollPanel>;
    };

    const searchKeyTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title">{t('product.search_key')}</span>
            {rowData.search_key}
        </React.Fragment>;
    };

    const categoryTemplate = (rowData) => {
        const style = selectedColumns.includes("category") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title" style={style}>{t('product.category')}</span>
            {rowData.category}
        </React.Fragment>;
    };

    const subCategoryTemplate = (rowData) => {
        const style = selectedColumns.includes("sub_category") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title" style={style}>{t('product.sub_category')}</span>
            {rowData.sub_category}
        </div>;
    };

    // The product's category path, as deep as the site's hierarchy runs.
    const categoriesTemplate = (rowData) => {
        const style = selectedColumns.includes("categories") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title" style={style}>{t('product.categories')}</span>
            {(rowData.categories || []).join(' > ')}
        </div>;
    };

    const typeTemplate = (rowData) => {
        const style = selectedColumns.includes("type") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title" style={style}>{t('product.type')}</span>
            {rowData.type}
        </div>;
    };

    const brandTemplate = (rowData) => {
        const style = selectedColumns.includes("brand") ? {} : {display:"inline-block"};
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title" style={style}>{t('product.brand')}</span>
            {rowData.brand}
        </React.Fragment>;
    };

    const tagsTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            {rowData.tags?.map(t => {
                return <Tag value={t} className="p-mr-1" rounded/>
            })}
        </React.Fragment>;
    };

    const productDiscountTypeTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            {rowData.discount_types?.map(t => {
                return t && <Tag value={t} className="p-mr-1" rounded/>
            })}
        </React.Fragment>;
    };

    const refIdTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title">{t('product.ref_id')}</span>
            {rowData.ref_id}
        </React.Fragment>;
    };

    const vatTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title">{t('product.vat')}</span>
            {rowData.vat}
        </React.Fragment>;
    };

    // Market search: show the lowest in-stock seller price. The backend already
    // computes competitorsMinPrice as the min over in-stock competitors (stock >= 1,
    // price > 0), and it ships as a flat field in the list payload.
    const marketPriceTemplate = (rowData) => {
        if (loading || !rowData) return <Skeleton />;
        return (
            <div style={{ textAlign: 'right' }}>
                <span className="p-column-title">{t('product.price')}</span>
                {rowData.competitorsMinPrice != null
                    ? formatCurrency(rowData.competitorsMinPrice, rowData.currency)
                    : <span style={{ color: '#9ca3af' }}>-</span>}
            </div>
        );
    };

    const productPriceTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        let color = "black";
        if (rowData.price && rowData.competitorsMinPrice && rowData.price < rowData.competitorsMinPrice )
            color= "green";
        else if (rowData.price && rowData.competitorsMinPrice && rowData.competitorsMaxPrice && rowData.price > rowData.competitorsMinPrice && rowData.price < rowData.competitorsMaxPrice )
            color= "firebrick";
        // else if (rowData.price && rowData.competitorsMinPrice && rowData.competitorsAvgPrice && rowData.price > rowData.competitorsAvgPrice && rowData.price < rowData.competitorsMaxPrice)
        //     color= "red";
        else if (rowData.price && rowData.competitorsMaxPrice && rowData.price > rowData.competitorsMaxPrice )
            color= "firebrick";

        return editingPrice_productID === rowData.id ?
            <div className="p-grid" style={{textAlign:'center'}}>
                <Loading/>
            </div> :
            <div className="p-text-nowrap p-text-right" style={{color: color}}>
                <span className="p-column-title">{t('product.price')}</span>
                {formatCurrency(rowData.price, rowData.currency)}
            </div>;
    };

    // Dispatches Enter on the enclosing cell so PrimeReact runs the column's
    // onEditorSubmit and closes the editor — same path as pressing Enter.
    const submitCellEdit = (e) => {
        const cell = e.target.closest('td');
        if (!cell) return;
        const enter = new KeyboardEvent('keydown', {bubbles: true});
        Object.defineProperty(enter, 'which', {value: 13});
        Object.defineProperty(enter, 'keyCode', {value: 13});
        cell.dispatchEvent(enter);
    };

    const violationStrategyEditor = (props) =>{

        const currency = props.rowData.violation_strategy && props.rowData.violation_strategy.currency ? props.rowData.violation_strategy.currency
            : props.rowData.currency && props.rowData.currency !== "" ? props.rowData.currency : user().currency;

        return <div style={{display:'flex', alignItems:'center', gap:'.25rem'}}>
            <Number id="min_value" name="min_value" value={violationStrategyMinValue}
                    onChange={(e) => {setViolationStrategyMinValue(getKeyValue(e).value);}} />
            {currency !== props.rowData.currency && currency}
            <Button icon="pi pi-check" className="p-button-rounded p-button-text p-button-success"
                    tooltip={t('actions.save')} tooltipOptions={{position: 'bottom'}}
                    onClick={submitCellEdit} />
        </div>;
    };

    // Pre-fill the editor with the value currently shown in the cell instead of 0.
    const onViolationStrategyEditorInit = (e) => {
        const rowData = e.columnProps.rowData;
        const vs = rowData?.violation_strategy;
        setViolationStrategyMinValue(vs && vs.min_value ? vs.min_value : (rowData.price || 0));
    };

    const onViolationStrategyEditorCancel = () => {
        setViolationStrategyMinValue(0);
    }

    const onViolationStrategyEditorSubmit = (e) => {
        let product = {...[...e.columnProps.value][e.columnProps.rowIndex]};

        let vs = product['violation_strategy'];

        const newValue = parseFloat(violationStrategyMinValue);
        // Skip when unchanged: matches the stored MAP, or (with no MAP yet) the
        // pre-filled product price, or was cleared to 0.
        if ((vs && parseFloat(vs.min_value) === newValue) ||
            (!vs && newValue === parseFloat(product.price)) ||
            newValue === 0) {
            setViolationStrategyMinValue(0);
            return;
        }

        if (vs && 'min_value' in vs)
            vs.min_value = violationStrategyMinValue;
        else
        {
            if (!vs)
                vs = {};
            vs.min_value = violationStrategyMinValue;
            vs.currency = product.currency && product.currency !== "" ? product.currency : user().currency;
        }

        setEditingPrice_productID(product.id);
        productService.savePriceViolation(product.id, vs).then(r => {

            products.find(p => p.id === product.id).violation_strategy = r.violation_strategy;
            products.find(p => p.id === product.id).price = r.calculated_violation_min_price;

            setProducts([...products])
            setViolationStrategyMinValue(0);

            toast.current.show({
                severity: 'success',
                summary: t('product_detail.save_violation_strategy'),
                detail: t('message_detail.successful')
            });
            setEditingPrice_productID("");
        }).catch(error => {
            setError(error);
            setError(null);
            setEditingPrice_productID("");
        });
    };



    const pricingStrategyMinValueTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return editingMinPrice_productID === rowData.id ?
            <div className="p-grid" style={{textAlign:'center'}}>
                <Loading/>
            </div> :
        <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('reprice.min_value')}</span>
            {rowData.pricing_strategy ? formatCurrency(rowData.pricing_strategy.min_value, rowData.currency): ""}
        </div>;
    };

    const pricingStrategyEditor = (props) =>{
        return <div>
            {pricingStrategyMinValueTemplate(props.rowData)}
            <Number id="min_value" name="min_value" value={pricingStrategyMinValue} onChange={(e) => {setPricingStrategyMinValue(getKeyValue(e).value);}} />
        </div>;
    };

    const onPricingStrategyEditorCancel = () => {
        setPricingStrategyMinValue(0);
    }

    const onPricingStrategyEditorSubmit = (e) => {
        let product = {...[...e.columnProps.value][e.columnProps.rowIndex]};

        let ps = product['pricing_strategy'];

        if ((ps && ps.min_value === parseFloat(pricingStrategyMinValue)) || parseFloat(pricingStrategyMinValue) === 0) {
            setPricingStrategyMinValue(0);
            return;
        }

        if (ps && 'min_value' in ps)
            ps.min_value = pricingStrategyMinValue;
        else
        {
            if (!ps)
                ps = {};
            ps.ignore_some_competitors = false;
            ps.is_active = false;
            ps.is_auto_update = false;
            ps.optimum_value = 0;
            ps.protection_strategy = "min_value";
            ps.reduction_type = "campaign";
            ps.reduction_value = 0;
            ps.min_value = pricingStrategyMinValue;
        }

        setEditingMinPrice_productID(product.id);

        productService.savePricingStrategy(product.id, ps).then(r => {

            products.find(p => p.id === product.id).pricing_strategy = ps;
            products.find(p => p.id === product.id).suggested_price = r.suggested_price;
            products.find(p => p.id === product.id).target_price = r.target_price;

            setProducts([...products]);

            setPricingStrategyMinValue(0);
            toast.current.show({
                severity: 'success',
                summary: t('reprice.save_pricing_strategy'),
                detail: t('message_detail.successful')
            });
            setEditingMinPrice_productID("");
        }).catch(error => {
            setError(error);
            setError(null);
            setEditingMinPrice_productID("");
        });
    }


    const pricingStrategyMaxValueTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return editingMaxPrice_productID === rowData.id ?
            <div className="p-grid" style={{textAlign:'center'}}>
                <Loading/>
            </div> :
            <div className="p-text-nowrap p-text-right">
                <span className="p-column-title">{t('reprice.max_value')}</span>
                {rowData.pricing_strategy ? formatCurrency(rowData.pricing_strategy.max_value, rowData.currency): ""}
            </div>;
    };

    const pricingStrategyMaxEditor = (props) =>{
        return <div>
            {pricingStrategyMaxValueTemplate(props.rowData)}
            <Number id="max_value" name="max_value" value={pricingStrategyMaxValue} onChange={(e) => {setPricingStrategyMaxValue(getKeyValue(e).value);}} />
        </div>;
    };

    const onPricingStrategyMaxEditorCancel = () => {
        setPricingStrategyMaxValue(0);
    };

    const onPricingStrategyMaxEditorSubmit = (e) => {

        let product = {...[...e.columnProps.value][e.columnProps.rowIndex]};

        let ps = product['pricing_strategy'];

        if ((ps && ps.max_value === parseFloat(pricingStrategyMaxValue)) || parseFloat(pricingStrategyMaxValue) === 0) {
            setPricingStrategyMaxValue(0);
            return;
        }

        if (ps && 'min_value' in ps)
            ps.max_value = pricingStrategyMaxValue;
        else
        {
            if (!ps)
                ps = {};
            ps.ignore_some_competitors = false;
            ps.is_active = false;
            ps.is_auto_update = false;
            ps.optimum_value = 0;
            ps.protection_strategy = "min_value";
            ps.reduction_type = "campaign";
            ps.reduction_value = 0;
            ps.min_value = 0;
            ps.max_value = pricingStrategyMaxValue;
        }

        setEditingMaxPrice_productID(product.id);
        productService.savePricingStrategy(product.id, ps).then(r => {

            products.find(p => p.id === product.id).pricing_strategy = ps;
            products.find(p => p.id === product.id).suggested_price = r.suggested_price;
            products.find(p => p.id === product.id).target_price = r.target_price;

            setProducts([...products]);
            setPricingStrategyMaxValue(0);
            toast.current.show({
                severity: 'success',
                summary: t('reprice.save_pricing_strategy'),
                detail: t('message_detail.successful')
            });
            setEditingMaxPrice_productID("");
        }).catch(error => {
            setError(error);
            setError(null);
            setEditingMaxPrice_productID("");
        });
    }

    const productTargetPriceTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        let suggestion_direction = "";
        if (parseFloat(rowData.price) > parseFloat(rowData.target_price))
            suggestion_direction = "down";
        else if (parseFloat(rowData.price) < parseFloat(rowData.target_price))
            suggestion_direction = "up";

        return <div data-tour="step6" className="p-text-nowrap p-text-right" style={{color: suggestion_direction ==="down" ? "red" : (suggestion_direction ==="up" ? "green" : "")}} >
            <span className="p-column-title">{t('product.target_price')}</span>
            {formatCurrency(rowData.target_price, rowData.currency)}&nbsp;<span className={suggestion_direction ==="down" ? "pi pi-arrow-down" : (suggestion_direction ==="up" ? "pi pi-arrow-up" : "")}/>
        </div>;
    };

    const productSuggestedPriceTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div data-tour="step6" style={{whiteSpace: 'nowrap'}} >
            <span className="p-column-title">{t('product.suggested_price')}</span>
            {formatCurrency(rowData.suggested_price, rowData.currency)}
        </div>;
    };

    const productMRPTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('price_violation.mrp_abbreviation')}</span>
            {formatCurrency(rowData?.violation_strategy?.max_value, rowData?.violation_strategy?.currency)}
        </div>;
    };

    const productCompMinNameTemplate = (rowData) => {
        if (loading)
            return <Skeleton/>;
        return <React.Fragment>
            <span className="p-column-title">{t('product.comp_min_name')}</span>
            {rowData.competitorsMinName}
        </React.Fragment>;
    };

    // Combined seller price column: min / avg / max stacked. When all three are
    // equal (or only a min exists) it collapses to a single value.
    const productCompRangeTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        const mn = rowData.competitorsMinPrice, av = rowData.competitorsAvgPrice, mx = rowData.competitorsMaxPrice;
        if (mn == null && av == null && mx == null)
            return <div>-</div>;
        const allSame = mn != null && av != null && mx != null && mn === av && av === mx;
        if (allSame || (av == null && mx == null))
            return <div className="pux-rangecell-single">
                <span className="p-column-title">{t('product.comp_range')}</span>
                {formatDecimal(mn)}
            </div>;
        const row = (cls, lab, val) => val == null ? null :
            <div className={"pux-rangecell-row " + cls}>
                <span className="pux-rangecell-lab">{lab}</span>
                <span className="pux-rangecell-val">{formatDecimal(val)}</span>
            </div>;
        return <div className="pux-rangecell">
            <span className="p-column-title">{t('product.comp_range')}</span>
            <div className="pux-rangecell-rows">
                {row('min', t('product.range_min'), mn)}
                {row('avg', t('product.range_avg'), av)}
                {row('max', t('product.range_max'), mx)}
            </div>
        </div>;
    };
    const productCompMinTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        let tooltip = selectedColumns.includes("competitorsMinName") ? "" : rowData.competitorsMinName;
        return <div data-tour="step7" data-toggle="tooltip" title={tooltip} className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.comp_min')}</span>
            {formatCurrency(rowData.competitorsMinPrice, rowData.currency)}
        </div>;
    };
    const productCompAvgTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.comp_avg')}</span>
            {formatCurrency(rowData.competitorsAvgPrice, rowData.currency)}
        </div>;
    };
    const productCompAvgDiffTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">{formatCurrency(rowData.competitorsAvgPriceDiff, rowData.currency)}</div>;
    };

    const productCompMinShippingPriceTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">{formatCurrency(rowData.competitorsMinShippingPrice, rowData.currency)}</div>;
    };

    const productCompMaxShippingPriceTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">{formatCurrency(rowData.competitorsMaxShippingPrice, rowData.currency)}</div>;
    };

    const productCompMaxNameTemplate = (rowData) => {
        if (loading)
            return <Skeleton/>;
        return <React.Fragment>
            <span className="p-column-title">{t('product.comp_max_name')}</span>
            {rowData.competitorsMaxName}
        </React.Fragment>;
    };

    const productCompMaxTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        let tooltip = selectedColumns.includes("competitorsMaxName") ? "" : rowData.competitorsMaxName;
        return <div data-tour="step8" data-toggle="tooltip" title={tooltip} className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.comp_max')}</span>
            {formatCurrency(rowData.competitorsMaxPrice, rowData.currency)}
        </div>;
    };

    const getPropertyTemplate = (rowData, e) =>{
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title">{t('product.' + e.field)}</span>
            {rowData.properties?.find(p=> p.name?.toLowerCase()===e.field.replace('properties.', ''))?.value}
        </div>;
    }

    const reviewNumberTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title">{t('product.review_number')}</span>
            {rowData.review_number}
        </div>;
    };

    const reviewTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title">{t('product.reviews')}</span>
            {rowData.review && rowData.review.rating_count}
        </div>;
    };

    const rateTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title">{t('product.rate')}</span>
            {rowData.review && rowData.review.rating}
        </div>;
    };

    const productDropShippingPriceTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.drop_shipping_price')}</span>
            {formatCurrency(rowData.drop_shipping_price, rowData.currency)}
        </div>;
    };

    const productDropShippingCostTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.drop_shipping_cost')}</span>
            {formatCurrency(rowData.drop_shipping_cost, rowData.currency)}
        </div>;
    };

    const productDropShippingReviewTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title">{t('product.drop_shipping_review')}</span>
            {rowData.drop_shipping && rowData.drop_shipping.rating_count}
        </div>;
    };

    const productDropShippingRateTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title">{t('product.drop_shipping_rate')}</span>
            {rowData.drop_shipping && rowData.drop_shipping.rating}
        </div>;
    };

    const productDropShippingBestSellersRankTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div>
            <span className="p-column-title">{t('product.drop_shipping_best_sellers_rank')}</span>
            {rowData.drop_shipping && rowData.drop_shipping.best_sellers_rank}
        </div>;
    };

    const positionTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title">{t('product.drop_shipping_best_sellers_rank')}</span>
            {rowData.position}
        </React.Fragment>;
    };

    // Buybox campaigns only: the owner's own buybox order (1 = winning the box).
    // Sourced from the summary's buybox_rank (owner competitor's live rank); null
    // when the owner sits below the tracked slots -- rendered as "-".
    const buyboxRankTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title">{t('product.buybox_rank')}</span>
            {rowData.buybox_rank != null
                ? <Tag severity={rowData.buybox_rank === 1 ? "success" : "info"} value={rowData.buybox_rank} />
                : '-'}
        </React.Fragment>;
    };

    // Buybox campaigns only: what the bidirectional controller is currently doing for
    // this product. Without this, "deliberately not competing right now" (waiting for
    // a floor-breaking competitor to run out, or holding at the top) is indistinguishable
    // from a stuck/broken product -- this is the primary signal support will be asked about.
    // Meta + label logic lives in BuyboxStateBadge, shared with the product detail page's
    // Dynamic Pricing tab so the two surfaces cannot drift apart.
    // Compact: state label only, no detail suffix/timestamps -- a list column needs to
    // stay scannable, not explanatory. The full text (detail + timestamps) is still one
    // hover away via the badge's native title tooltip.
    const buyboxStateTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title">{t('product.buybox_state')}</span>
            <BuyboxStateBadge buyboxState={rowData.buybox_state} t={t} compact />
        </React.Fragment>;
    };

    const shelvePositionTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <React.Fragment>
            <span className="p-column-title">{t('product.shelve_position')}</span>
            {rowData.current_shelve_position}
        </React.Fragment>;
    };

    const myStockTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.my_stock')}</span>
            {rowData.stock != null ? rowData.stock : "-"}
        </div>;
    };

    // Seller-count cell doubles as the inline sellers expander (replaces the one
    // that used to live in the Status column).
    const competitorsCountTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        const count = rowData.competitorsCount != null ? rowData.competitorsCount : (rowData.competitors ? rowData.competitors.length : 0);
        return <div data-tour="step9" className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.comp_count')}</span>
            {rowData.variant_matching_needed ? <i className="pi pi-images" style={{color:'#dc2626', marginRight:6}} title={t('product.variant_available')} /> : null}
            {count > 0
                ? <button type="button" className="pux-sellerbtn" title={t('product.seller')}
                          onClick={(e) => { e.stopPropagation(); detail(rowData, 'sellers'); }}>
                    <i className="pi pi-users" /><span>{count}</span>
                  </button>
                // Buybox competitors come only from the marketplace API -- a manual
                // seller URL would be meaningless, so no add affordance here.
                : isBuyboxCampaign
                ? <span>{count}</span>
                : <Button label={t('product_detail.add_competitor')} icon="pi pi-plus"
                          className="p-button-outlined p-button-sm"
                          onClick={(e) => { e.stopPropagation(); openAddSeller(rowData); }} />}
        </div>;
    };
    const urlCountTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;

        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.url_count')}</span>
            {rowData.url_count}
        </div>;
    };
    const competitorSuggestionsCountTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        const count = rowData.competitorSuggestionsCount;
        return <div data-tour="step10" className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.suggest_count')}</span>
            {count > 0
                ? <button type="button" className="pux-sugbtn" title={t('product.suggest_count')}
                          onClick={(e) => { e.stopPropagation(); detail(rowData, 'suggestions'); }}>
                    <i className="pi pi-list" /><span>{count}</span>
                  </button>
                : <span style={{color:'#9ca3af'}}>{count != null ? count : 0}</span>}
        </div>;
    }
    const suppliersCountTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        const count = rowData.suppliersCount || 0;
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.supplier_count')}</span>
            {count > 0
                ? <button type="button" className="pux-sellerbtn" title={t('product.supplier_count')}
                          onClick={(e) => { e.stopPropagation(); detail(rowData, 'suppliers'); }}>
                    <i className={tabIcon.suppliers} /><span>{count}</span>
                  </button>
                : "-"}
        </div>;
    };
    const supplierSuggestionsCountTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        const count = rowData.supplierSuggestionsCount || 0;
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.supplier_suggest_count')}</span>
            {count > 0
                ? <button type="button" className="pux-sellerbtn" title={t('product.supplier_suggest_count')}
                          onClick={(e) => { e.stopPropagation(); detail(rowData, 'suppliers'); }}>
                    <i className={tabIcon.suppliers} /><span>{count}</span>
                  </button>
                : "-"}
        </div>;
    };
    const statusTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t('product.status')}</span>
            {rowData.is_active ? t('actions.active') : t('actions.passive')}
        </div>;
    };

    // ----- product-ux redesign signal cells ------------------------------------
    const statusCellTemplate = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div>
            {isMarketplaceProfile && <MarketplaceStatusLines row={rowData} t={t} />}
            <StatusCell row={rowData} profileKey={profile.key} t={t} templateNameById={templateNameById} />
            {/* Inline sellers expander lives in the Status column. */}
            <div style={{marginTop:'6px'}}>{sellerExpander(rowData)}</div>
        </div>;
    };
    const priceBandCell = (rowData) => {
        if (loading)
            return <Skeleton />;
        return <div style={{ textAlign: 'right' }}>{rowData.price ? formatCurrency(rowData.price, rowData.currency) : ''}</div>;
    };
    // EFS Elektronik tiered-discount cell: own price (rowData.price) × rate.
    const tieredPriceCell = (rowData, rate) => {
        if (loading)
            return <Skeleton />;
        return <div style={{ textAlign: 'right' }}>{rowData.price ? formatCurrency(Math.round(rowData.price * rate * 100) / 100, rowData.currency) : ''}</div>;
    };
    const suggestionCell = (rowData) => {
        if (loading)
            return <Skeleton />;
        // The suggestion is still computed for a paused product -- it is only the
        // automatic sending that stops -- so the row shows the number plus why it
        // is not going anywhere.
        return <span style={{display:'inline-flex', alignItems:'center', gap:6, whiteSpace:'nowrap'}}>
            <SuggestionAction row={rowData} currency={rowData?.violation_strategy?.currency || rowData.currency || ''} />
            {rowData.is_auto_update_paused &&
            <i className="pi pi-pause" style={{fontSize:'.7rem', color:'#d97706'}} title={t('reprice.auto_update_paused')} />}
            {getActivePriceSendHold(rowData) &&
            <i className="pi pi-clone" style={{fontSize:'.7rem', color:'#d97706'}}
               title={t('reprice.price_send_hold_short', {campaign: rowData.price_send_hold.winner_campaign_name || '-'})} />}
        </span>;
    };

    const rowMenuItem = ({ icon, color, bg, label, danger, onClick }) => (
        <button type="button" className="pux-rowmenu-item" onClick={onClick}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', background: 'none',
                         padding: '8px', cursor: 'pointer', textAlign: 'left', borderRadius: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
                           borderRadius: 8, background: bg, color }}>
                <i className={icon} />
            </span>
            <span style={{ fontSize: '.85rem', color: danger ? '#dc2626' : '#374151', fontWeight: 500 }}>{label}</span>
        </button>
    );

    const actionTemplate = (rowData) => {
        if (loading)
            return <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                <Skeleton shape="circle" width="2.5rem" height="1.5rem" />
                <Skeleton shape="circle" width="2.5rem" height="2.5rem" />
                <Skeleton shape="circle" width="2.5rem" height="2.5rem" />
        </div>;
        const scraping_request = scrapingRequests[rowData.id]
        const productUrl = scraping_request?.url || rowData.product_url
        let newProductRequest = null
        if (productUrl && productUrl !== "no url") {
            newProductRequest = {
                url: productUrl,
                urls: [productUrl],
                type: "manuel_product_create",
                campaign_id: rowData.campaign_id,
                product_id: rowData.id};
        }

        return <div className="pux-actions" style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
            <span className="p-column-title">{t('table_headers.action')}</span>
            {rowData.name ?
                <Button icon="pi pi-pencil" data-tour="step12" className="p-button-outlined p-button-rounded p-button-success" tooltipOptions={{position: 'bottom'}} tooltip={t('actions.edit')} onClick={() => showUpdate(rowData)}/>
                :
                <ScrapeStatus type="product" scraping_request={scraping_request}
                campaign_id={rowData.campaign_id}
                product_id={rowData.id}
                competitor_url={scraping_request?.url}
                newRequest={newProductRequest}
                newRequestCompleted={() => getNotDoneProductScrapingRequests()} />
            }
            <Button icon="pi pi-ellipsis-v" className="p-button-outlined p-button-rounded p-button-secondary" data-tour="step13"
                    tooltipOptions={{position: 'bottom'}} tooltip={t('actions.more')}
                    onClick={(e) => { setMenuRow(rowData); rowMenu.current.toggle(e); }}/>
        </div>;
    };

    const onSort = (e) =>{
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
        setExpandedRows(null);
    }
    //#endregion DataTable Items

   const mystock = [
        {label: t('actions.all'), value: null},
        {label: t('product.stock_in'), value: 'stock_in'},
        {label: t('product.stock_out'), value: 'stock_out'},
        {label: t('product.invalid_link'), value: 'invalid_link'},
    ];

    const reprice = [
        {label: t('actions.all'), value: null},
        {label: t('product.reprice_dec'), value: true},
        {label: t('product.reprice_inc'), value: false},
    ];

    const myprice = [
        {label: t('product.myprice_highest'), value: 'highest'},
        {label: t('product.myprice_avg_higher'), value: 'avg_higher'},
        {label: t('product.myprice_average'), value: 'average'},
        {label: t('product.myprice_avg_lower'), value: 'avg_lower'},
        {label: t('product.myprice_same'), value: 'same'},
        {label: t('product.myprice_lowest'), value: 'lowest'},
    ];

    const isActiveOptions = [
        {label: t('actions.active'), value: true},
        {label: t('actions.passive'), value: false}
    ];

    const isDropShippingLoss = [
        {label: t('actions.all'), value: null},
        {label: t('product.loss'), value: true},
        {label: t('product.profit'), value: false}
    ];

    const isDropShippingSupplied = [
        {label: t('actions.all'), value: null},
        {label: t('product.stock_in'), value: true},
        {label: t('product.stock_out'), value: false}
    ];

    const hasCompetitors = [
        {label: t('actions.all'), value: null},
        {label: t('product.has_competitor'), value: true},
        {label: t('product.no_competitor'), value: false}
    ];

    // A product "has a supplier" when it carries at least one supplier CHILD row.
    // The parent row is the source product on the supplier site, not an offer,
    // and the server's suppliersCount already excludes it.
    const hasSuppliers = [
        {label: t('actions.all'), value: null},
        {label: t('product.has_supplier'), value: true},
        {label: t('product.no_supplier'), value: false}
    ];

    // Supplier status choices. Both together: no seller in stock on the site,
    // whether or not the product was ever matched there. Stock alone counts.
    const supplierStatusOptions = [
        {label: t('product.supplier_status_no_match'), value: 'no_match'},
        {label: t('product.supplier_status_no_stock'), value: 'no_stock'}
    ];

    // The price-protection floor column (pricing_strategy.min_value): the floor
    // the cheapest supplier offers set.
    const hasMinValues = [
        {label: t('actions.all'), value: null},
        {label: t('product.has_min_value'), value: true},
        {label: t('product.no_min_value'), value: false}
    ];

    const isVariantMatchingNeeded = [
        {label: t('actions.all'), value: null},
        {label: t('product.needs_variant_matching'), value: true},
        {label: t('product.no_variant_matching'), value: false}
    ];

    const violationOptions = [
        {label: t('actions.all'), value: null},
        {label: t('product.filter_violation_map_label'), value: 'map'},
        {label: t('product.filter_violation_mrp_label'), value: 'mrp'},
        {label: t('product.filter_violation_none'), value: 'none'},
        {label: t('product.filter_violation_any'), value: 'any'},
    ];

    // Marketplace filter group options. "Cheaper on" lists the counted
    // marketplaces: the configured competitors, or every marketplace on the
    // list when none is configured (an empty list counts them all).
    const mpFirstPartyOptions = [
        {label: t('product.marketplace.first_party_present', operatorTextOptions(selectedCampaign?.settings)), value: 'present'},
        {label: t('product.marketplace.first_party_missing', operatorTextOptions(selectedCampaign?.settings)), value: 'missing'},
    ];
    const mpStatusOptions = [
        {label: t('product.marketplace.filter_status_leader'), value: 'leader'},
        {label: t('product.marketplace.filter_status_behind'), value: 'behind'},
        {label: t('product.marketplace.filter_status_alone'), value: 'alone'},
    ];
    const mpBuyboxOptions = [
        {label: t('product.marketplace.filter_buybox_own', operatorTextOptions(selectedCampaign?.settings)), value: 'own'},
        {label: t('product.marketplace.filter_buybox_third_party'), value: 'third_party'},
    ];
    // The cached counts belong to one campaign; another campaign's are not shown.
    const mpCheaperOnCounts = cheaperOnCache && selectedCampaign && cheaperOnCache.campaignId === selectedCampaign.id
        ? cheaperOnCache.counts : null;
    const mpCountedKeys = isMarketplaceProfile
        ? (orderedMarketplaceKeys([], marketplaceConfig).length > 0
            ? orderedMarketplaceKeys([], marketplaceConfig)
            : orderedMarketplaceKeys(products, marketplaceConfig))
        : [];
    const mpCheaperOnOptions = cheaperOnOptionList({
        keys: mpCountedKeys, counts: mpCheaperOnCounts, selected: filters.mp_cheaper_on,
        anyLabel: t('product.marketplace.cheaper_on_any'), labelOf: marketplaceLabel,
    });

    // Resolve a human label for a single-value dimension from its option array.
    const optionLabel = (options, value) => {
        const found = options.find(o => o.value === value);
        return found ? found.label : String(value);
    };

    // A full category URL would swamp the filter bar; its head and tail are
    // enough to recognise which link the list is showing.
    const shortenUrl = (url) => (url.length <= 45 ? url : url.slice(0, 21) + '...' + url.slice(-21));

    // Active filters as removable chips. Each chip resets ONE dimension to its
    // default via the existing onFilterChange. globalFilter is excluded (search box).
    const buildActiveFilters = () => {
        const chips = [];
        const reset = (name, value) => () => onFilterChange({target:{name, value}});

        if (filters.is_active !== defaultFilters.is_active)
            chips.push({key:'is_active', label: optionLabel(isActiveOptions, filters.is_active), onRemove: reset('is_active', defaultFilters.is_active)});
        if (filters.mystock !== defaultFilters.mystock)
            chips.push({key:'mystock', label: optionLabel(mystock, filters.mystock), onRemove: reset('mystock', defaultFilters.mystock)});
        if (filters.reprice !== defaultFilters.reprice)
            chips.push({key:'reprice', label: optionLabel(reprice, filters.reprice), onRemove: reset('reprice', defaultFilters.reprice)});
        if (filters.auto_update_paused !== defaultFilters.auto_update_paused)
            chips.push({key:'auto_update_paused', label: t('reprice.auto_update_paused'), onRemove: reset('auto_update_paused', defaultFilters.auto_update_paused)});
        if (filters.hasCompetitor !== defaultFilters.hasCompetitor)
            chips.push({key:'hasCompetitor', label: optionLabel(hasCompetitors, filters.hasCompetitor), onRemove: reset('hasCompetitor', defaultFilters.hasCompetitor)});
        if (filters.hasSupplier !== defaultFilters.hasSupplier)
            chips.push({key:'hasSupplier', label: optionLabel(hasSuppliers, filters.hasSupplier), onRemove: reset('hasSupplier', defaultFilters.hasSupplier)});
        if (filters.hasMinValue !== defaultFilters.hasMinValue)
            chips.push({key:'hasMinValue', label: optionLabel(hasMinValues, filters.hasMinValue), onRemove: reset('hasMinValue', defaultFilters.hasMinValue)});
        if ((filters.supplierWebsites || []).length > 0)
            chips.push({key:'supplierWebsites', label: `${t('product.supplier_website')} (${filters.supplierWebsites.length})`, onRemove: reset('supplierWebsites', defaultFilters.supplierWebsites)});
        // One chip for the pair: the filter only applies with a site AND a status.
        if ((filters.supplierStatusWebsites || []).length > 0 && (filters.supplierStatuses || []).length > 0)
            chips.push({key:'supplierStatus',
                        label: `${t('product.supplier_status')}: ${supplierStatusOptions.filter(o => filters.supplierStatuses.includes(o.value)).map(o => o.label).join(' / ')} (${filters.supplierStatusWebsites.length})`,
                        onRemove: () => setFilters({...filters, supplierStatusWebsites: defaultFilters.supplierStatusWebsites,
                                                    supplierStatuses: defaultFilters.supplierStatuses})});
        if (filters.variant_matching_needed !== defaultFilters.variant_matching_needed)
            chips.push({key:'variant_matching_needed', label: optionLabel(isVariantMatchingNeeded, filters.variant_matching_needed), onRemove: reset('variant_matching_needed', defaultFilters.variant_matching_needed)});
        if (filters.drop_shipping_loss !== defaultFilters.drop_shipping_loss)
            chips.push({key:'drop_shipping_loss', label: optionLabel(isDropShippingLoss, filters.drop_shipping_loss), onRemove: reset('drop_shipping_loss', defaultFilters.drop_shipping_loss)});
        if (filters.drop_shipping_supplied !== defaultFilters.drop_shipping_supplied)
            chips.push({key:'drop_shipping_supplied', label: optionLabel(isDropShippingSupplied, filters.drop_shipping_supplied), onRemove: reset('drop_shipping_supplied', defaultFilters.drop_shipping_supplied)});
        if (filters.violationOnly !== defaultFilters.violationOnly)
            chips.push({key:'violationOnly', label: optionLabel(violationOptions, filters.violationOnly), onRemove: reset('violationOnly', defaultFilters.violationOnly)});
        if (filters.unauthorizedOnly !== defaultFilters.unauthorizedOnly)
            chips.push({ key:'unauthorizedOnly', label: t('product.filter_unauthorized'), onRemove: reset('unauthorizedOnly', defaultFilters.unauthorizedOnly) });
        if (filters.noMap !== defaultFilters.noMap)
            chips.push({ key:'noMap', label: t('product.filter_no_map', 'MAP tanımsız'), onRemove: reset('noMap', defaultFilters.noMap) });
        filters.myprice.forEach(v => {
            chips.push({key:'myprice_'+v, label: optionLabel(myprice, v), onRemove: () => onFilterChange({target:{name:'myprice', value: filters.myprice.filter(x => x !== v)}})});
        });
        if (filters.website.length > 0)
            chips.push({key:'website', label: `${t('product.website')} (${filters.website.length})`, onRemove: reset('website', defaultFilters.website)});
        if (filters.discount_types.length > 0)
            chips.push({key:'discount_types', label: `${t('product.discount_type')} (${filters.discount_types.length})`, onRemove: reset('discount_types', defaultFilters.discount_types)});
        if (filters.cheapestCompetitor !== null && filters.cheapestCompetitor !== '')
            chips.push({key:'cheapestCompetitor', label: `${t('product.cheapestCompetitor')}: ${filters.cheapestCompetitor}`, onRemove: reset('cheapestCompetitor', defaultFilters.cheapestCompetitor)});
        if (filters.mp_cheaper_on != null && filters.mp_cheaper_on !== '')
            chips.push({key:'mp_cheaper_on',
                label: t('product.marketplace.chip_cheaper_on', {marketplace: filters.mp_cheaper_on === 'any'
                    ? t('product.marketplace.cheaper_on_any') : marketplaceLabel(filters.mp_cheaper_on)}),
                onRemove: reset('mp_cheaper_on', defaultFilters.mp_cheaper_on)});
        if (filters.mp_min_diff_pct != null && filters.mp_min_diff_pct !== '')
            chips.push({key:'mp_min_diff_pct', label: t('product.marketplace.chip_min_diff_pct', {value: filters.mp_min_diff_pct}),
                onRemove: reset('mp_min_diff_pct', defaultFilters.mp_min_diff_pct)});
        if (filters.mp_first_party != null && filters.mp_first_party !== '')
            chips.push({key:'mp_first_party', label: optionLabel(mpFirstPartyOptions, filters.mp_first_party),
                onRemove: reset('mp_first_party', defaultFilters.mp_first_party)});
        if (filters.mp_status != null && filters.mp_status !== '')
            chips.push({key:'mp_status',
                label: t('product.marketplace.chip_status', {value: optionLabel(mpStatusOptions, filters.mp_status)}),
                onRemove: reset('mp_status', defaultFilters.mp_status)});
        if (filters.mp_buybox != null && filters.mp_buybox !== '')
            chips.push({key:'mp_buybox', label: optionLabel(mpBuyboxOptions, filters.mp_buybox),
                onRemove: reset('mp_buybox', defaultFilters.mp_buybox)});
        if (importUrl)
            chips.push({key:'importUrl', label: `${t('product.filter_import_url')}: ${shortenUrl(importUrl)}`,
                        onRemove: () => setImportUrl('')});
        if (filters.pricing_template != null && filters.pricing_template !== defaultFilters.pricing_template)
            chips.push({key:'pricing_template',
                label: `${t('pricing_template.filter_label')}: ${filters.pricing_template === '__none__'
                    ? t('pricing_template.filter_none')
                    : (templateNameById[filters.pricing_template] || t('pricing_template.tag_generic'))}`,
                onRemove: reset('pricing_template', defaultFilters.pricing_template)});
        return chips;
    };
    const activeFilters = buildActiveFilters();

    // Profile quick filters: each applies/reverts a predefined set on EXISTING
    // dimensions (no new predicate logic, except the violationOnly clause).
    const quickFilterDefs = {
        loss: {
            label: t('product.loss'),
            active: filters.drop_shipping_loss === true,
            toggle: () => onFilterChange({target:{name:'drop_shipping_loss', value: filters.drop_shipping_loss === true ? defaultFilters.drop_shipping_loss : true}}),
        },
        inStock: {
            label: t('product.stock_in'),
            active: filters.mystock === 'stock_in',
            toggle: () => onFilterChange({target:{name:'mystock', value: filters.mystock === 'stock_in' ? defaultFilters.mystock : 'stock_in'}}),
        },
        // 'unauthorized' and 'recent' have no existing filter dimension -> skipped.
    };
    const quickFilters = (profile.quickFilters || [])
        .filter(key => quickFilterDefs[key])
        .map(key => ({key, label: quickFilterDefs[key].label, active: quickFilterDefs[key].active, onToggle: quickFilterDefs[key].toggle}));

    // For competition profiles, promote the price-position chips + reprice
    // direction to be always-visible inline in the filter bar.
    const repriceChip = (value, labelKey, icon, colorCls) => {
        const on = filters.reprice === value;
        return (
            <button type="button"
                className={`pux-chip pux-chip-toggle ${on ? colorCls : 'off'}`}
                onClick={() => onFilterChange({target:{name:'reprice', value: on ? defaultFilters.reprice : value}})}>
                <i className={`pi ${icon}`} style={{ fontSize:'.7rem' }} /> {t(labelKey)}
            </button>
        );
    };
    const competitionInline = (profile.filterGroups || []).includes('competition') ? (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            <MyPriceChips t={t} value={filters.myprice} onChange={(val) => onFilterChange({target:{name:'myprice', value: val}})} />
            <span style={{ width:1, height:18, background:'#e5e7eb', margin:'0 2px' }} />
            {repriceChip(false, 'product.reprice_inc', 'pi-arrow-up', 'pux-chip-green')}
            {repriceChip(true, 'product.reprice_dec', 'pi-arrow-down', 'pux-chip-amber')}
        </div>
    ) : null;

    // For brand (compliance) profiles, promote the violation filters to be
    // always-visible inline toggle chips in the filter bar.
    const violationChip = (value, labelKey, colorCls) => {
        const on = filters.violationOnly === value;
        return (
            <button type="button" className={`pux-chip pux-chip-toggle ${on ? colorCls : 'off'}`}
                    onClick={() => onFilterChange({target:{name:'violationOnly', value: on ? defaultFilters.violationOnly : value}})}>
                {t(labelKey)}
            </button>
        );
    };
    const complianceInline = (profile.filterGroups || []).includes('compliance') ? (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            {violationChip('map', 'product.filter_violation_map_label', 'pux-chip-red')}
            {violationChip('mrp', 'product.filter_violation_mrp_label', 'pux-chip-red')}
            {violationChip('none', 'product.filter_violation_none', 'pux-chip-green')}
            {(() => {
              const on = filters.unauthorizedOnly === true;
              return (
                <button type="button" className={`pux-chip pux-chip-toggle ${on ? 'pux-chip-violet' : 'off'}`}
                        onClick={() => onFilterChange({target:{name:'unauthorizedOnly', value: on ? defaultFilters.unauthorizedOnly : true}})}>
                  <i className="pi pi-user-minus" /> {t('product.filter_unauthorized')}
                </button>
              );
            })()}
        </div>
    ) : null;

    // Stock quick-toggle chips, shown inline for every profile.
    const stockChip = (value, labelKey, colorCls) => {
        const on = filters.mystock === value;
        return (
            <button type="button" className={`pux-chip pux-chip-toggle ${on ? colorCls : 'off'}`}
                    onClick={() => onFilterChange({target:{name:'mystock', value: on ? defaultFilters.mystock : value}})}>
                {t(labelKey)}
            </button>
        );
    };
    const stockInline = (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            {stockChip('stock_in', 'product.stock_in', 'pux-chip-green')}
            {stockChip('stock_out', 'product.stock_out', 'pux-chip-red')}
        </div>
    );
    const profileInline = competitionInline || complianceInline;
    const inlineFilters = (
        <React.Fragment>
            {stockInline}
            {profileInline && <span style={{ width:1, height:18, background:'#e5e7eb', margin:'0 2px' }} />}
            {profileInline}
        </React.Fragment>
    );

    // Options for the pricing-template filter: "No template" + one entry per
    // template the user has (resolved from the already-loaded id->name map).
    const templateFilterOptions = [
        {label: t('pricing_template.filter_none'), value: '__none__'},
        ...Object.entries(templateNameById).map(([id, name]) => ({label: name, value: id})),
    ];

    // Inline filter panel groups, driven by profile.filterGroups. Each render()
    // reuses the existing controls and onFilterChange wiring.
    const filterGroupDefs = {
        template: {
            label: t('pricing_template.filter_label'),
            visible: () => Object.keys(templateNameById).length > 0,
            render: () => {
                const selId = filters.pricing_template;
                const selected = selId && selId !== '__none__' ? templateById[selId] : null;
                return (
                    <React.Fragment>
                        <Dropdown value={filters.pricing_template} options={templateFilterOptions} showClear
                                  placeholder={t('pricing_template.filter_all')} style={{width:'100%'}}
                                  onChange={(e) => {onFilterChange({target:{name:"pricing_template", value: e.value == null ? null : e.value}})}}/>
                        {selected &&
                        <div style={{marginTop:6, fontSize:'.78rem', color:'#7c3aed', display:'flex', alignItems:'center', gap:4}}>
                            <i className="pi pi-bolt" style={{fontSize:'.72rem'}}/> {templateRuleSummary(selected, t)}
                        </div>}
                    </React.Fragment>
                );
            },
        },
        status: {
            label: t('product.status'),
            visible: () => true,
            render: () => {
                // Active/Passive is no longer a forced binary choice: a single "Passive"
                // toggle. When off (default), products are Active in the background.
                const passiveOn = filters.is_active === false;
                return (
                    <React.Fragment>
                        <button type="button" className={`pux-chip pux-chip-toggle ${passiveOn ? 'pux-chip-gray' : 'off'}`}
                                onClick={() => onFilterChange({target:{name:"is_active", value: passiveOn ? true : false}})}>
                            {t('actions.passive')}
                        </button>
                        <div style={{marginTop:8}}>
                            {/* Stock In/Out are inline bar chips; the panel only offers the invalid-link option. */}
                            <SelectButton value={filters.mystock} optionLabel="label" options={mystock.filter(o => o.value === 'invalid_link')}
                                          onChange={(e) => {onFilterChange({target:{name:"mystock", value:e.value}})}}/>
                        </div>
                        <div style={{marginTop:8}}>
                            {/* Products whose automatic price sending the merchant paused. */}
                            <button type="button" className={`pux-chip pux-chip-toggle ${filters.auto_update_paused === true ? 'pux-chip-amber' : 'off'}`}
                                    onClick={() => onFilterChange({target:{name:"auto_update_paused", value: filters.auto_update_paused === true ? null : true}})}>
                                <i className="pi pi-pause" /> {t('reprice.auto_update_paused')}
                            </button>
                        </div>
                    </React.Fragment>
                );
            },
        },
        competition: {
            label: t('product.competition'),
            visible: () => true,
            render: () => (
                <React.Fragment>
                    <SelectButton value={filters.hasCompetitor} optionLabel="label" options={hasCompetitors.filter(o => o.value !== null)}
                                  onChange={(e) => {onFilterChange({target:{name:"hasCompetitor", value:e.value}})}}/>
                    {filterWebSites && filterWebSites.length > 0 &&
                    <div style={{marginTop:8}}>
                        <MultiSelect name="website" value={filters.website} options={filterWebSites} onChange={onFilterChange} filter={true} maxSelectedLabels={2} placeholder={t('product.website')} style={{width:'100%'}}/>
                    </div>}
                    <div style={{marginTop:8}}>
                        <InputText name="cheapestCompetitor" value={filters.cheapestCompetitor} onChange={onFilterChange} placeholder={t('product.cheapestCompetitor')} style={{width:'100%'}}/>
                    </div>
                    <div style={{marginTop:8}}>
                        <SelectButton value={filters.variant_matching_needed} optionLabel="label" options={isVariantMatchingNeeded.filter(o => o.value !== null)}
                                      onChange={(e) => {onFilterChange({target:{name:"variant_matching_needed", value:e.value}})}}/>
                    </div>
                </React.Fragment>
            ),
        },
        sellers: {
            // Market search: competitors found by scraping are the sellers, so the
            // has-competitor predicate (competitorsCount) doubles as has-seller.
            label: t('product.sellers'),
            visible: () => true,
            render: () => (
                <SelectButton value={filters.hasCompetitor} optionLabel="label" options={hasCompetitors.filter(o => o.value !== null)}
                              onChange={(e) => {onFilterChange({target:{name:"hasCompetitor", value:e.value}})}}/>
            ),
        },
        supplier: {
            // Suppliers are not competitors: a competitor is someone we price
            // against, a supplier is someone we buy from, and the cheapest few
            // supplier offers set the product's price floor. Only campaigns
            // running the supplier module have any of this.
            label: t('product.supplier'),
            visible: () => isSupplierModuleActive,
            render: () => (
                <React.Fragment>
                    <SelectButton value={filters.hasSupplier} optionLabel="label" options={hasSuppliers.filter(o => o.value !== null)}
                                  onChange={(e) => {onFilterChange({target:{name:"hasSupplier", value:e.value}})}}/>
                    {filterSupplierWebSites && filterSupplierWebSites.length > 0 &&
                    <div style={{marginTop:8}}>
                        <MultiSelect name="supplierWebsites" value={filters.supplierWebsites || []} options={filterSupplierWebSites}
                                     onChange={onFilterChange} filter={true} maxSelectedLabels={2}
                                     placeholder={t('product.supplier_website')} style={{width:'100%'}}/>
                    </div>}
                    <div style={{marginTop:8}}>
                        <SelectButton value={filters.hasMinValue} optionLabel="label" options={hasMinValues.filter(o => o.value !== null)}
                                      onChange={(e) => {onFilterChange({target:{name:"hasMinValue", value:e.value}})}}/>
                    </div>
                    {filterSupplierWebSites && filterSupplierWebSites.length > 0 &&
                    <div style={{marginTop:12}}>
                        <div style={{fontWeight:600, marginBottom:4}}>{t('product.supplier_status')}</div>
                        <MultiSelect name="supplierStatusWebsites" value={filters.supplierStatusWebsites || []} options={filterSupplierWebSites}
                                     onChange={onFilterChange} filter={true} maxSelectedLabels={2}
                                     placeholder={t('product.supplier_status_website')} style={{width:'100%'}}/>
                        <SelectButton value={filters.supplierStatuses || []} optionLabel="label" options={supplierStatusOptions} multiple
                                      style={{marginTop:8}}
                                      onChange={(e) => {onFilterChange({target:{name:"supplierStatuses", value:e.value || []}})}}/>
                        <div style={{fontSize:'0.85em', opacity:0.7, marginTop:4}}>{t('product.supplier_status_hint')}</div>
                    </div>}
                </React.Fragment>
            ),
        },
        dropshipping: {
            label: t('product.drop_shipping'),
            visible: () => true,
            render: () => (
                <React.Fragment>
                    <SelectButton value={filters.drop_shipping_loss} optionLabel="label" options={isDropShippingLoss.filter(o => o.value !== null)}
                                  onChange={(e) => {onFilterChange({target:{name:"drop_shipping_loss", value:e.value}})}}/>
                    <div style={{marginTop:8}}>
                        <SelectButton value={filters.drop_shipping_supplied} optionLabel="label" options={isDropShippingSupplied.filter(o => o.value !== null)}
                                      onChange={(e) => {onFilterChange({target:{name:"drop_shipping_supplied", value:e.value}})}}/>
                    </div>
                </React.Fragment>
            ),
        },
        marketplace: {
            // Marketplace operator: server-side filters on the mp_* summary fields.
            label: t('product.marketplace.filter_title'),
            visible: () => isMarketplaceProfile,
            render: () => (
                <React.Fragment>
                    <Dropdown value={filters.mp_cheaper_on} options={mpCheaperOnOptions} showClear
                              placeholder={t('product.marketplace.cheaper_on')} style={{width:'100%'}}
                              onChange={(e) => {onFilterChange({target:{name:"mp_cheaper_on", value: e.value == null ? null : e.value}})}}/>
                    <div style={{marginTop:8}}>
                        {/* onValueChange fires on blur / Enter, so typing does not refetch per keystroke. */}
                        <InputNumber value={filters.mp_min_diff_pct} mode="decimal" maxFractionDigits={2} suffix="%"
                                     placeholder={t('product.marketplace.min_diff_pct')} style={{width:'100%'}} inputStyle={{width:'100%'}}
                                     onValueChange={(e) => {onFilterChange({target:{name:"mp_min_diff_pct", value: e.value == null ? null : e.value}})}}/>
                    </div>
                    <div style={{marginTop:8}}>
                        <SelectButton value={filters.mp_first_party} optionLabel="label" options={mpFirstPartyOptions}
                                      onChange={(e) => {onFilterChange({target:{name:"mp_first_party", value: e.value == null ? null : e.value}})}}/>
                    </div>
                    <div style={{marginTop:8}}>
                        <Dropdown value={filters.mp_status} options={mpStatusOptions} showClear
                                  placeholder={t('product.marketplace.filter_status')} style={{width:'100%'}}
                                  onChange={(e) => {onFilterChange({target:{name:"mp_status", value: e.value == null ? null : e.value}})}}/>
                    </div>
                    <div style={{marginTop:8}}>
                        <Dropdown value={filters.mp_buybox} options={mpBuyboxOptions} showClear
                                  placeholder={t('product.marketplace.filter_buybox')} style={{width:'100%'}}
                                  onChange={(e) => {onFilterChange({target:{name:"mp_buybox", value: e.value == null ? null : e.value}})}}/>
                    </div>
                </React.Fragment>
            ),
        },
        classification: {
            label: t('product.classification'),
            visible: () => [filterCategories, filterBrands, filterTags, filterDiscountTypes].some(a => a && a.length > 0),
            render: () => (
                <React.Fragment>
                    {filterCategories && filterCategories.length > 0 &&
                    <MultiSelect name="category" value={filters.category} options={filterCategories}
                                 optionGroupLabel="label" optionGroupChildren="items"
                                 onChange={onFilterChange} filter={true} maxSelectedLabels={2}
                                 placeholder={t('product.category')} style={{width:'100%'}}/>}
                    {filterBrands && filterBrands.length > 0 &&
                    <div style={{marginTop:8}}>
                        <MultiSelect name="brand" value={filters.brand} options={filterBrands} onChange={onFilterChange} filter={true} maxSelectedLabels={2} placeholder={t('product.brand')} style={{width:'100%'}}/>
                    </div>}
                    {filterTags && filterTags.length > 0 &&
                    <div style={{marginTop:8}}>
                        <MultiSelect name="tags" value={filters.tags} options={filterTags} onChange={onFilterChange} filter={true} maxSelectedLabels={2} placeholder={t('product.tags')} style={{width:'100%'}}/>
                    </div>}
                    {filterDiscountTypes && filterDiscountTypes.length > 0 &&
                    <div style={{marginTop:8}}>
                        <MultiSelect name="discount_types" value={filters.discount_types} options={filterDiscountTypes} onChange={onFilterChange} filter={true} maxSelectedLabels={2} placeholder={t('product.discount_type')} style={{width:'100%'}}/>
                    </div>}
                </React.Fragment>
            ),
        },
        priceRange: {
            // No existing price-range dimension/predicate. Reuse the existing
            // created_at range inputs as the available "range" controls; a real
            // price min/max would require a new predicate clause (out of scope).
            label: t('product.created_at'),
            visible: () => true,
            render: () => (
                <React.Fragment>
                    <Calendar name="created_at_start" value={filters.created_at_start} onChange={onFilterChange} showButtonBar={true} placeholder={t('product.created_at_start')} style={{width:'100%'}}/>
                    <div style={{marginTop:8}}>
                        <Calendar name="created_at_end" value={filters.created_at_end} onChange={onFilterChange} showButtonBar={true} placeholder={t('product.created_at_end')} style={{width:'100%'}}/>
                    </div>
                </React.Fragment>
            ),
        },
    };
    const filterPanelGroups = (profile.filterGroups || [])
        .filter(key => filterGroupDefs[key] && (filterGroupDefs[key].visible ? filterGroupDefs[key].visible() : true))
        .map(key => ({key, label: filterGroupDefs[key].label, render: filterGroupDefs[key].render}));

    // Manufacturer "price" is the MAP (editing it sets the violation min price).
    const priceHeader = <div>
        <Tooltip tooltip={t('product.violation_price_edit')} icon="pi-pencil"/>
        {t('price_violation.map_abbreviation')}
    </div>;

    const protectionLimitHeader = <div>
        <Tooltip tooltip={t('product.min_price_edit')} icon="pi-pencil"/>
        {t('reprice.min_value')}
    </div>;

    const protectionLimitMaxHeader = <div>
        <Tooltip tooltip={t('product.max_price_edit')} icon="pi-pencil"/>
        {t('reprice.max_value')}
    </div>;

    const suggestedPriceHeader = <div>
        <Tooltip tooltip={t('product.suggested_price_tooltip')}/>
        {t('product.suggested_price')}
    </div>;

    const selectColumnsButton = <Button icon="pi pi-bars" className="p-button-outlined" tooltip={t('product.fields_to_show')} tooltipOptions={{position: 'bottom'}} onClick={(e) => op.current.toggle(e)} />;

    // Intentionally empty: column picker is reachable via the table-header ☰ icon.
    // Phase-2 will add Table/Card view toggle items here.

    const excelFieldsOverlay = (
        <OverlayPanel ref={opExcelFields} style={{width:"25em", height: '30em'}}>
            <div className="p-grid">
                <div className="p-col-8">
                <ListBox value={exportFields}
                             filterPlaceholder={t('product.fields_to_export')} fixedPlaceholder={true}
                             options={exportableFields(t, selectedCampaign)}
                         multiple filter
                         listStyle={{maxHeight:'24em'}}
                         maxSelectedLabels={0}
                             onChange={(e) => {
                                 localStorage.setItem("exportFields", JSON.stringify(e.value));
                                 setExportFields(e.value);
                             }} />
                </div>
                <div className="p-col-4">

                <Button icon="pi pi-refresh" tooltip={t('actions.reset')}
                        tooltipOptions={{position: 'bottom'}} className="p-button-outlined p-button-help p-mr-1"
                        onClick={()=>{
                            const exportFields = getSelectedExportableFields(t, selectedCampaign, true);
                            localStorage.setItem("exportFields", JSON.stringify(exportFields));
                            setExportFields(exportFields);
                        }} />
                <Button icon="pi pi-file-excel" tooltip={t('actions.excel')}
                        tooltipOptions={{position: 'bottom'}} className="p-button-outlined p-button-success"
                        onClick={exportList} />
                </div>
            </div>

        </OverlayPanel>
    );



    tableRowsRef.current = loading ? skeletonArray : filteredProducts;
    // The marketplace profile sorts the Status column by the buybox diff %; {} elsewhere.
    const statusColumnProps = marketplaceStatusColumnProps(isMarketplaceProfile, () => tableRowsRef.current);

    return <React.Fragment>

            {/* Re-key so the tooltip re-scans the DOM after rows render (async load). */}
            <PrimeTooltip key={`sugg-tt-${filteredProducts.length}`} target=".pux-sugg-tip" position="top" />

            <div className="card" style={{marginBottom:(showMergePanel && productsToMerge.length > 0) ? "250px" : ""}} hidden={page === pages.detail} >

                <Banner/>

                {loadingMessage &&
                    <div style={{ height: '35px' }} >
                        <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                        {t('product.items_loaded', {count: products.length === 0 ? "" : productTotal ? `${products.length} / ${productTotal}` : products.length.toString()})}
                    </div>
                }

                <ProductToolbar
                    t={t}
                    globalFilter={filters.globalFilter}
                    onGlobalFilter={(e) => onFilterChange({target:{name:'globalFilter', value:e.target.value}})}
                    onAdd={showAdd}
                    onRefresh={() => refresh()}
                    onExcel={(e) => isMarketplaceProfile ? exportMarketplaceList() : opExcelFields.current.toggle(e)}
                    viewMode={viewMode}
                    onViewMode={onViewMode}
                    addButton={isShopifyCampaign ? (
                        <Button label={t('shopify.add_from_store', 'Add from Shopify store')}
                                icon="pi pi-shopping-cart"
                                className="p-button-success"
                                onClick={() => setShowShopifyDialog(true)} />
                    ) : isBuyboxCampaign ? (
                        <Button label={buyboxManageLabel}
                                icon="pi pi-box"
                                className="p-button-success"
                                onClick={() => setShowMarketplaceProductDialog(true)} />
                    ) : undefined}
                />

                <ProductFilterChips
                    t={t}
                    activeFilters={activeFilters}
                    quickFilters={quickFilters}
                    inlineNode={inlineFilters}
                    onOpenPanel={() => setShowFilters(s => !s)}
                    onClearAll={onClear}
                />

                <ProductSelectionBar
                    t={t}
                    count={selectedRows.length}
                    primaryActions={[
                        { key: 'classify', label: t('actions.update_all', {number:''}), icon: 'pi pi-pencil', command: () => { setBulkInitialTab(1); setShowBulkOperations(true); } },
                        { key: 'move', label: t('actions.move_products', {number:''}), icon: 'pi pi-upload', command: () => { setBulkInitialTab(0); setShowBulkOperations(true); } },
                        ...((profile.bulkActions || []).includes('strategy')
                            ? [{ key: 'strategy', label: t('pricing_template.assign_strategy'), icon: 'pi pi-clone',
                                 className: 'p-button-help', command: () => setShowApplyTemplate(true) }]
                            : []),
                    ]}
                    overflowItems={[
                        { label: t('actions.activate'), desc: t('product.bulk_activate_desc'), icon: 'pi pi-check-circle', color: '#16a34a', bg: '#dcfce7', command: () => bulkSetActive(false) },
                        { label: t('actions.deactivate'), desc: t('product.bulk_deactivate_desc'), icon: 'pi pi-ban', color: '#d97706', bg: '#fef3c7', command: () => bulkSetActive(true) },
                        ...((profile.bulkActions || []).includes('strategy')
                            ? [{ separator: true }, { label: t('pricing_template.remove'), desc: t('pricing_template.remove_desc'), icon: 'pi pi-times-circle', color: '#7c3aed', bg: '#ede9fe', command: () => bulkDetachTemplate() }]
                            : []),
                        { separator: true },
                        { label: t('actions.remove'), desc: t('product.bulk_remove_desc'), icon: 'pi pi-trash', color: '#dc2626', bg: '#fee2e2', danger: true, command: () => bulkRemove() },
                    ]}
                    onClear={() => setSelectedRows([])}
                />

                <ProductFilterPanel
                    t={t}
                    visible={showFilters}
                    groups={filterPanelGroups}
                    onClose={() => setShowFilters(false)}
                />

                {excelFieldsOverlay}

                <OverlayPanel ref={rowMenu} style={{ width: '16em' }}>
                    {menuRow && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {rowMenuItem({ icon: 'pi pi-power-off',
                                color: menuRow.is_active ? '#d97706' : '#16a34a',
                                bg: menuRow.is_active ? '#fef3c7' : '#dcfce7',
                                label: menuRow.is_active ? t('actions.deactivate') : t('actions.activate'),
                                onClick: () => { rowMenu.current.hide(); changeIsActive(menuRow); } })}
                            {rowMenuItem({ icon: 'pi pi-clone', color: '#0891b2', bg: '#cffafe', label: t('actions.merge'),
                                onClick: () => {
                                    rowMenu.current.hide();
                                    if (productsToMerge.findIndex(p => p.id === menuRow.id) === -1) {
                                        productsToMerge.push({ ...menuRow, keepAsMine: productsToMerge.length === 0 });
                                        setProductsToMerge([...productsToMerge]);
                                    }
                                    setShowMergePanel(true);
                                } })}
                            {rowMenuItem({ icon: 'pi pi-trash', color: '#dc2626', bg: '#fee2e2', danger: true, label: t('actions.delete'),
                                onClick: () => {
                                    rowMenu.current.hide();
                                    confirmDeleteProduct(menuRow);
                                } })}
                        </div>
                    )}
                </OverlayPanel>

                {viewMode === 'card' ? (
                    <div>
                        <div className="pux-card-grid">
                            {(loading ? [] : filteredProducts.slice(first, first + rows)).map(p => (
                                <ProductCard key={p.id} row={p} profileKey={profile.key} t={t} templateNameById={templateNameById}
                                             highlighted={p.id === selectedProductId}
                                             onOpen={(row) => selectProduct({ data: row, originalEvent: { target: null } })}
                                             onShowSellers={setSellersDialogRow} />
                            ))}
                        </div>
                        <Dialog header={sellersDialogRow ? sellersDialogRow.name : ''} visible={!!sellersDialogRow}
                                style={{ width: '60em', maxWidth: '95vw' }} modal onHide={() => setSellersDialogRow(null)}>
                            {sellersDialogRow && (profile.key === 'brand'
                                ? <ViolatorRows row={sellersDialogRow} t={t} />
                                : isMarketplaceProfile
                                ? <MarketplaceRows row={sellersDialogRow} t={t} config={marketplaceConfig} settings={selectedCampaign?.settings} />
                                : <CompetitorRows row={sellersDialogRow} t={t} showComparison={profile.key !== 'market'}
                                                  highlightMine={profile.key === 'competitive' || profile.key === 'dropshipping'} />)}
                        </Dialog>
                        <div className="pux-card-footer">{t('product.footer', {count: products.length.toString(), filteredCount: filteredProducts.length.toString()})}</div>
                        <Paginator first={first} rows={rows} totalRecords={filteredProducts.length}
                                   rowsPerPageOptions={[10, 20, 50, 100]}
                                   onPageChange={(e) => { setFirst(e.first); setRows(e.rows); }} />
                    </div>
                ) : (
                <div data-tour="step2">
                    <DataTable value={loading ? skeletonArray : filteredProducts}
                               className={`pux-table-sticky${profile.key === 'market' ? ' pux-market' : ''}`}
                               autoLayout={true}
                               paginator={true}
                               rows={rows}
                               first={first}
                               rowsPerPageOptions={[10, 20, 50, 100]}
                               onPage={(e) => {
                                   localStorage.setItem("paginator", JSON.stringify(e.rows));
                                   setFirst(e.first);
                                   setRows(e.rows);
                               }}
                               footer={t('product.footer', {count: products.length.toString(), filteredCount: filteredProducts.length.toString()})}
                               selectionMode="checkbox"
                               selection={selectedRows}
                               onSelectionChange={(e) => setSelectedRows(e.value)}
                               onRowClick={selectProduct}
                               rowClassName={(data) => ({'pux-row-visited': !!(data && data.id === selectedProductId)})}
                               sortField={sortField}
                               sortOrder={sortOrder}
                               onSort={onSort}
                               ref={dtProducts}
                               expandedRows={expandedRows}
                               onRowToggle={(e) => setExpandedRows(e.data)}
                               rowExpansionTemplate={(data) => profile.key === 'brand'
                                   ? <ViolatorRows row={data} t={t} />
                                   : isMarketplaceProfile
                                   ? <MarketplaceRows row={data} t={t} config={marketplaceConfig} settings={selectedCampaign?.settings} />
                                   : <CompetitorRows row={data} t={t} showComparison={profile.key !== 'market'} highlightMine={profile.key === 'competitive' || profile.key === 'dropshipping'} />}
                               editMode="cell">
                        <Column selectionMode="multiple" className="preventSelect pux-sticky pux-sticky-c1" />
                        <Column body={indexTemplate} header={selectColumnsButton} className="pux-sticky pux-sticky-c2" />
                        <Column header={t('product.status_col')} body={statusCellTemplate} className="pux-sticky pux-sticky-c3" {...statusColumnProps} />
                        {selectedColumns.includes("name") && <Column field="name" header={t('product.product_name')} body={productNameTemplate} sortable className="pux-sticky pux-sticky-c4" />}
                        {selectedColumns.includes("image") && <Column field="image" header={t('product.image')} body={productImageTemplate} headerStyle={{width:'10em'}}/>}
                        {selectedColumns.includes("website") && <Column field="website" header={t('product.website')} body={productWebsiteTemplate} sortable  />}
                        {selectedColumns.includes("sku") && <Column field="sku" header={t('product.sku')} body={skuTemplate} sortable  />}
                        {selectedColumns.includes("mpn") && <Column field="mpn" header={t('product.mpn')} body={mpnTemplate} sortable  />}
                        {selectedColumns.includes("ean") && <Column field="ean" header={t('product.ean')} body={eanTemplate} sortable  />}
                        {selectedColumns.includes("upc") && <Column field="upc" header={t('product.upc')} body={upcTemplate} sortable  />}
                        {selectedColumns.includes("asin") && <Column field="asin" header={t('product.asin')} body={asinTemplate} sortable  />}
                        {selectedColumns.includes("barcode") && <Column field="barcode" header={t('product.barcode')} body={barcodeTemplate} sortable />}

                        {selectedColumns.includes("properties") && <Column field="properties" header={t('product.properties.')} body={variantTemplate} sortable  style={{width:'10%'}} />}
                        {selectedColumns.includes("description") && <Column field="description" header={t('product.description')} body={productDescriptionTemplate} sortable  style={{width:'20%'}} />}
                        {selectedColumns.includes("search_key") && <Column field="search_key" header={t('product.search_key')} body={searchKeyTemplate} sortable  style={{width:'10%'}} />}
                        {selectedColumns.includes("category") && <Column field="category" header={t('product.category')} body={categoryTemplate} sortable  />}
                        {selectedColumns.includes("sub_category") && <Column field="sub_category" header={t('product.sub_category')} body={subCategoryTemplate} sortable  />}
                        {selectedColumns.includes("categories") && <Column field="categories" header={t('product.categories')} body={categoriesTemplate} sortable  />}
                        {selectedColumns.includes("type") && <Column field="type" header={t('product.type')} body={typeTemplate} sortable  />}
                        {selectedColumns.includes("brand") && <Column field="brand" header={t('product.brand')} body={brandTemplate} sortable  />}
                        {selectedColumns.includes("tags") && <Column field="tags" header={t('product.tags')} body={tagsTemplate} style={{textAlign:"center"}} sortable  />}
                        {selectedColumns.includes("properties.size") && <Column field="properties.size" header={t('product.properties.size')} body={getPropertyTemplate} sortable  />}
                        {selectedColumns.includes("properties.color") && <Column field="properties.color" header={t('product.properties.color')} body={getPropertyTemplate} sortable  />}
                        {selectedColumns.includes("properties.variation_number") && <Column field="properties.variation_number" header={t('product.properties.variation_number')} body={getPropertyTemplate} sortable  />}
                        {selectedColumns.includes("properties.package_type") && <Column field="properties.package_type" header={t('product.properties.package_type')} body={getPropertyTemplate} sortable  />}
                        {selectedColumns.includes("properties.presentation") && <Column field="properties.presentation" header={t('product.properties.presentation')} body={getPropertyTemplate} sortable  />}

                        {selectedColumns.includes("reviews") && <Column field="review.rating_count" header={t('product.reviews')} headerStyle={{textAlign:"center"}} body={reviewTemplate} sortable  />}
                        {selectedColumns.includes("rate") && <Column field="review.rating" header={t('product.rate')} headerStyle={{textAlign:"center"}} body={rateTemplate} sortable  />}
                        {selectedColumns.includes("review_number") && <Column field="review_number" header={t('product.review_number')} headerStyle={{textAlign:"center"}} body={reviewNumberTemplate} sortable  />}
                        {selectedColumns.includes("ref_id") && <Column field="ref_id" header={t('reprice.ref_id')} body={refIdTemplate} sortable  />}
                        {selectedColumns.includes("vat") && <Column field="vat" header={t('product.vat')} body={vatTemplate} sortable  />}
                        {isCampaignDropShipping(selectedCampaign) && selectedColumns.includes("drop_shipping_price") && <Column field="drop_shipping_price" header={t('product.drop_shipping_price')} headerStyle={{textAlign:"right"}} body={productDropShippingPriceTemplate} sortable  />}
                        {isCampaignDropShipping(selectedCampaign) &&  selectedColumns.includes("drop_shipping_cost") && <Column field="drop_shipping_cost" header={t('product.drop_shipping_cost')} headerStyle={{textAlign:"right"}} body={productDropShippingCostTemplate} sortable  />}
                        {isCampaignDropShipping(selectedCampaign) &&  selectedColumns.includes("drop_shipping_review") && <Column field="drop_shipping_review"  header={t('product.drop_shipping_review')} headerStyle={{textAlign:"center"}} body={productDropShippingReviewTemplate} sortable  />}
                        {isCampaignDropShipping(selectedCampaign) &&  selectedColumns.includes("drop_shipping_rate") && <Column field="drop_shipping_rate"  header={t('product.drop_shipping_rate')} headerStyle={{textAlign:"center"}} body={productDropShippingRateTemplate} sortable  />}
                        {isCampaignDropShipping(selectedCampaign) &&  selectedColumns.includes("drop_shipping_best_sellers_rank") && <Column field="drop_shipping_best_sellers_rank"  header={t('product.drop_shipping_best_sellers_rank')} headerStyle={{textAlign:"left"}} body={productDropShippingBestSellersRankTemplate} sortable  />}
                        {!isMarketplaceProfile && (isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && selectedColumns.includes("position") && <Column field="position" header={t('product.position')} body={positionTemplate} sortable  />}
                        {isMarketplaceProfile && marketplaceColumns({
                            t, rows: filteredProducts, campaign: selectedCampaign, loading,
                            getRows: () => tableRowsRef.current,
                            onSellersClick: (row, key) => setMpSellersDialog({row, key, visible: true}),
                        })}
                        {!isMarketplaceProfile && isBuyboxCampaign && <Column field="buybox_rank" header={t('product.buybox_rank')} headerStyle={{textAlign:"center"}} style={{textAlign:"center"}} body={buyboxRankTemplate} />}
                        {!isMarketplaceProfile && isBuyboxCampaign && <Column field="buybox_state.state" header={t('product.buybox_state')} headerStyle={{textAlign:"center"}} style={{textAlign:"center"}} body={buyboxStateTemplate} />}
                        {selectedColumns.includes("shelve_position") && <Column field="current_shelve_position" header={t('product.shelve_position')} body={shelvePositionTemplate} sortable  />}
                        {isCampaignManufacturer(selectedCampaign) && selectedColumns.includes("price") && <Column field="price" header={priceHeader} body={productPriceTemplate} headerStyle={{textAlign:"right"}} sortable  className="preventSelect" editor={violationStrategyEditor} onEditorInit={onViolationStrategyEditorInit} onEditorSubmit={onViolationStrategyEditorSubmit} onEditorCancel={onViolationStrategyEditorCancel} />}
                        {isCampaignManufacturer(selectedCampaign) && selectedColumns.includes("mrp") && <Column field="mrp" header={t('price_violation.mrp_abbreviation')} headerStyle={{textAlign:"right"}} body={productMRPTemplate} sortable  />}
                        {(isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && isColumnShown("price") && <Column field="price" header={t('product.my_price')} body={priceBandCell} headerStyle={{textAlign:"right"}} sortable />}
                        {(isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && isColumnShown("my_stock") && <Column field="stock" header={t('product.my_stock')} headerStyle={{textAlign:"right"}} body={myStockTemplate} sortable />}
                        {(isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && isEfsElektronikUser() &&
                            EFS_TIERED_PRICE_COLUMNS.filter(col => selectedColumns.includes(col.value)).map(col =>
                                <Column key={col.value} field={col.value} header={col.label} headerStyle={{textAlign:"right"}}
                                        body={(rowData) => tieredPriceCell(rowData, col.rate)} />)}
                        {!isMarketplaceProfile && isCampaignMarketSearch(selectedCampaign) && selectedColumns.includes("price") && <Column field="price" header={t('product.price')} body={marketPriceTemplate} headerStyle={{textAlign:"right"}} />}
                        {(isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && isColumnShown("pricing_strategy_min_value") && <Column field="pricing_strategy.min_value" header={protectionLimitHeader} headerStyle={{textAlign:"right"}} body={pricingStrategyMinValueTemplate} sortable  className="preventSelect" editor={pricingStrategyEditor} onEditorSubmit={onPricingStrategyEditorSubmit} onEditorCancel={onPricingStrategyEditorCancel} />}
                        {(isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && isColumnShown("pricing_strategy_max_value") && <Column field="pricing_strategy.max_value" header={protectionLimitMaxHeader} headerStyle={{textAlign:"right"}} body={pricingStrategyMaxValueTemplate} sortable  className="preventSelect" editor={pricingStrategyMaxEditor} onEditorSubmit={onPricingStrategyMaxEditorSubmit} onEditorCancel={onPricingStrategyMaxEditorCancel}/>}
                        {(isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && isColumnShown("target_price") && <Column field="target_price" header={t('product.target_price')} headerStyle={{textAlign:"right"}} body={productTargetPriceTemplate} sortable  />}
                        {(isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && isColumnShown("suggested_price") && <Column field="suggested_price" header={suggestedPriceHeader} headerStyle={{textAlign:"right"}} body={suggestionCell} sortable  />}


                        {isColumnShown("discount_type") && <Column field="discount_types" header={t('product.discount_type')} body={productDiscountTypeTemplate} sortable  />}
                        {isColumnShown("competitorsRange") && <Column field="competitorsMinPrice" header={t('product.comp_range')} body={productCompRangeTemplate} sortable  />}
                        {isColumnShown("competitorsMinName") && <Column field="competitorsMinName" header={t('product.comp_min_name')} body={productCompMinNameTemplate} sortable  />}
                        {isColumnShown("competitorsMinPrice") && <Column field="competitorsMinPrice" header={t('product.comp_min')} headerStyle={{textAlign:"right"}} body={productCompMinTemplate} sortable  />}
                        {isColumnShown("competitorsMinShippingPrice") && <Column field="competitorsMinShippingPrice" header={t('product.comp_min_shipping_price')} headerStyle={{textAlign:"right"}} body={productCompMinShippingPriceTemplate} sortable  />}
                        {isColumnShown("competitorsAvgPrice") && <Column field="competitorsAvgPrice" header={t('product.comp_avg')} headerStyle={{textAlign:"right"}} body={productCompAvgTemplate} sortable  />}
                        {isColumnShown("competitorsAvgPriceDiff") && <Column field="competitorsAvgPriceDiff" header={t('product.comp_avg_diff')} headerStyle={{textAlign:"right"}} body={productCompAvgDiffTemplate} sortable  />}
                        {isColumnShown("competitorsMaxName") && <Column field="competitorsMaxName" header={t('product.comp_max_name')} body={productCompMaxNameTemplate} sortable  />}
                        {isColumnShown("competitorsMaxPrice") && <Column field="competitorsMaxPrice" header={t('product.comp_max')} headerStyle={{textAlign:"right"}} body={productCompMaxTemplate} sortable  />}
                        {isColumnShown("competitorsMaxShippingPrice") && <Column field="competitorsMaxShippingPrice" header={t('product.comp_max_shipping_price')} headerStyle={{textAlign:"right"}} body={productCompMaxShippingPriceTemplate} sortable  />}
                        {isColumnShown("url_count") && <Column field="url_count" header={t('product.url_count')} headerStyle={{textAlign:"right"}} body={urlCountTemplate} sortable  />}
                        {isColumnShown("competitorsCount") && <Column field="competitorsCount" header={t('product.comp_count')} headerStyle={{textAlign:"right"}} body={competitorsCountTemplate} sortable  />}
                        {selectedColumns.includes("competitorSuggestionsCount") && <Column field="competitorSuggestionsCount" header={t('product.suggest_count')} headerStyle={{textAlign:"right"}} body={competitorSuggestionsCountTemplate} sortable  />}
                        {selectedColumns.includes("suppliersCount") && <Column field="suppliersCount" header={t('product.supplier_count')} headerStyle={{textAlign:"right"}} body={suppliersCountTemplate} sortable />}
                        {selectedColumns.includes("supplierSuggestionsCount") && <Column field="supplierSuggestionsCount" header={t('product.supplier_suggest_count')} headerStyle={{textAlign:"right"}} body={supplierSuggestionsCountTemplate} sortable />}
                        {selectedColumns.includes("is_active") && <Column field="is_active" header={t('product.status')} headerStyle={{textAlign:"right", width:'7em'}} body={statusTemplate} />}
                        <Column body={actionTemplate} header={t('table_headers.action')} headerStyle={{textAlign:"center", width:'8em'}} className="preventSelect"/>
                    </DataTable>
                </div>
                )}

                <OverlayPanel ref={op} style={{ maxHeight:'30em', overflow:'auto' }}>
                    <ColumnPicker
                        t={t}
                        allColumns={productListColumns(t, selectedCampaign).filter(col => isColumnAllowedForProfile(profile.key, col.value))}
                        profile={profile}
                        selected={selectedColumns}
                        onChange={(cols) => { localStorage.setItem('selectedColumns', JSON.stringify(cols)); setSelectedColumns(cols); }}
                        onResetToProfile={() => { localStorage.removeItem('selectedColumns'); setSelectedColumns(getSelectedProductListColumns(t, selectedCampaign, true, profile.columnGroups)); }}
                    />
                </OverlayPanel>

                <Dialog header={t('product_detail.add_competitor')} visible={!!addSellerRow} modal
                        style={{width:'30em', maxWidth:'95vw'}} onHide={() => setAddSellerRow(null)}
                        footer={<Button label={t('actions.save')} icon="pi pi-check" className="p-button-success"
                                        disabled={!(addSellerUrl || '').trim()} onClick={saveAddSeller}/>}>
                    <div style={{display:'flex', flexDirection:'column', gap:8}}>
                        <label htmlFor="addSellerUrl">{t('product_detail.add_competitor')}</label>
                        <InputText id="addSellerUrl" value={addSellerUrl} autoFocus placeholder="https://..."
                                   onChange={(e) => setAddSellerUrl(e.target.value)}
                                   onKeyDown={(e) => { if (e.key === 'Enter') saveAddSeller(); }}/>
                    </div>
                </Dialog>

                <Sidebar showCloseIcon={false} visible={page === pages.add} style={{width:'26em', overflow: "auto"}} position="right" onHide={cancel}>
                    <ProductAdd cancel={cancel}  add={add} />
                </Sidebar>

                <Sidebar showCloseIcon={false} visible={page === pages.edit} style={{width:'25em', overflow: "scroll"}} position="right" onHide={cancel}>
                    <ProductEdit selectedProduct={selectedProduct}
                                 categories={categories}
                                 brands={brands}
                                 cancel={cancel} update={update} />
                </Sidebar>

            </div>

            {page === pages.detail &&
                <ProductDetail product_id={selectedProductId} initialTab={detailInitialTab} onDetailComplete={detailComplete} goToPrevious={goToPrevious} goToNext={goToNext} />
            }


            {showBulkOperations &&
                <ProductBulkOperations filteredProducts={filteredProducts}
                                       preselectedItems={selectedRows}
                                       initialTab={bulkInitialTab}
                                       refreshProducts={()=> {
                    getNotDoneProductScrapingRequests();
                    setSelectedRows([]);
                    refresh();}}
                                       sortField={dtProducts.current && dtProducts.current.state ? dtProducts.current.state.sortField : ""}
                                       sortOrder={dtProducts.current && dtProducts.current.state ? dtProducts.current.state.sortOrder : "1"}
                                       hideDialog={() => {setShowBulkOperations(false);}} />}

            <ApplyTemplateDialog
                visible={showApplyTemplate}
                productIds={selectedRows.map(s => s.id)}
                onHide={() => setShowApplyTemplate(false)}
                onApplied={(updates) => { setSelectedRows([]); patchAffectedProducts(updates); }}
                t={t}
            />

            {showMergePanel && productsToMerge.length > 0 &&
                <Sidebar visible={true} position="bottom" baseZIndex={100000}
                         style={{height:'20em', overflow: "scroll"}}
                         onHide={() => setShowMergePanel(false)}>
                    <ProductMerge productsToMerge={productsToMerge}
                                  removeFromMerge={(p)=>{
                                      setProductsToMerge(productsToMerge.filter(product => product.id !== p.id));
                                  }}
                                  setAsInitial={(p)=>{
                                      productsToMerge.forEach(product=>{
                                          product.keepAsMine = product.id === p.id;
                                      });
                                      setProductsToMerge([...productsToMerge]);
                                  }}
                                  cancel={() => {
                                      setShowMergePanel(false);
                                      setProductsToMerge([]);
                                  }}
                                  merge={() => {
                                      confirmDialog({
                                          message: t('dialogs.product.merge_msg'),
                                          header: t('dialogs.product.merge'),
                                          icon: 'pi pi-exclamation-triangle',
                                          acceptClassName: 'p-button-danger',
                                          accept: () => mergeProducts(),
                                          acceptLabel: t('yes_no.yes'),
                                          rejectLabel: t('yes_no.no')
                                      });
                                  }}
                    />
                </Sidebar>
            }



            {isMarketplaceProfile &&
                <MarketplaceSellersDialog
                    visible={!!(mpSellersDialog && mpSellersDialog.visible)}
                    onHide={() => setMpSellersDialog(d => (d ? {...d, visible: false} : d))}
                    t={t}
                    campaignId={selectedCampaign && selectedCampaign.id}
                    product={mpSellersDialog ? {
                        id: mpSellersDialog.row.id,
                        name: mpSellersDialog.row.name,
                        // "Mine" in the dialog is the operator's own (1P) offer.
                        price: mpSellersDialog.row.mp_first_party_price,
                        currency: marketplaceCurrency(mpSellersDialog.row),
                    } : null}
                    marketplaceKey={mpSellersDialog ? mpSellersDialog.key : null}
                    label={mpSellersDialog ? ownAwareMarketplaceLabel(mpSellersDialog.key, selectedCampaign?.settings) : undefined} />}

            <ShopifyProductDialog
                visible={showShopifyDialog}
                campaignId={selectedCampaign && selectedCampaign.id}
                onHide={() => setShowShopifyDialog(false)}
                onSaved={() => { refresh(); }}
            />

            <MarketplaceProductDialog
                visible={showMarketplaceProductDialog}
                campaignId={selectedCampaign && selectedCampaign.id}
                onHide={() => setShowMarketplaceProductDialog(false)}
                onChanged={() => refresh()}
            />

        </React.Fragment>
}
export default Product;
